<template>
  <div class="telegram-session-screen" :data-telegram-state="state">
    <div class="telegram-chat-shell"
      :class="{ 'tg-drag-over': isDragOver, 'is-pinned-panel-open': pinnedPanelOpen }"
      @dragenter.prevent="isDragOver = true"
      @dragover.prevent
      @dragleave="onDragLeave"
      @drop.prevent="handleDrop"
      @click.capture="onShellClickCapture"
    >
      <div class="telegram-chat-head">
        <div class="telegram-chat-head-main">
          <div
            class="telegram-chat-avatar"
            :class="[avatarHeadClass, { 'group-clickable': profile && profile.type === 'group' }]"
            :style="avatarHeadStyle"
            v-html="avatarHeadHtml"
            @click="profile && profile.type === 'group' ? openMemberManagerFromHead() : null"
            :title="profile && profile.type === 'group' ? '群成员管理' : ''"
          ></div>
          <div class="telegram-chat-copy" @click="profile && profile.type === 'group' ? openMemberManagerFromHead() : null" :style="profile && profile.type === 'group' ? 'cursor:pointer' : ''">
            <div class="telegram-chat-title">
              <template v-if="profile && profile.type === 'group'">
                {{ chatHeadTitle }}
              </template>
              <template v-else>
                {{ profile ? profile.title : '' }}
                <span v-if="profile && profile.type === 'agent'" class="tiny-tag agent-tag" style="margin-left:6px">Agent</span>
              </template>
            </div>
            <div class="telegram-chat-sub">
              <template v-if="profile && profile.type === 'group'">
                群组 · {{ onlineCount != null ? onlineCount + ' 人在线' : '获取中…' }}
              </template>
              <template v-else-if="profile && profile.type === 'coworker'">
                <span class="tg-coworker-online-dot" :class="{ online: currentSession && currentSession.peerOnline }"></span>
                {{ currentSession && currentSession.peerOnline ? '在线' : '离线' }}
              </template>
              <template v-else>{{ profile ? profile.sub : '' }}</template>
            </div>
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:6px">
          <button class="chat-search-btn" type="button" aria-label="搜索聊天记录" @click.stop="toggleChatSearch">
            <svg viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="1.8"/><path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          </button>
          <button class="telegram-head-action" type="button" aria-label="更多" @click.stop="openGroupMenu">⋯</button>
          <button class="workspace-collapse-btn" type="button" aria-label="折叠工作区" @click.stop="toggleWorkspace">
            <svg viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" stroke-width="1.8"/><line x1="15" y1="3" x2="15" y2="21" stroke="currentColor" stroke-width="1.8"/></svg>
          </button>
        </div>
      </div>

      <!-- 置顶消息 bar：贴在 head 下、body 上 -->
      <PinnedMessageBar
        :pinned-key="pinnedKey"
        @open-panel="openPinnedPanel"
        @dismiss="dismissPinnedBar"
      />

      <div class="telegram-chat-body-wrap">
        <!-- 未读跳转按钮 -->
        <button v-if="unreadJumpVisible" class="unread-jump-btn" type="button" @click="jumpToUnread">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
          {{ unreadJumpCount }} 条新消息
        </button>
        <div class="telegram-chat-body" :id="'telegram' + stateCap + 'Body'" ref="body" @contextmenu.prevent>
        <div class="telegram-chat-flow">
          <div v-if="chatLoading" class="telegram-empty-messages"><div class="tg-chat-spinner"></div>加载中…</div>
          <div v-else-if="!messages || messages.length === 0" class="telegram-empty-messages">暂无消息</div>
          <div v-if="isLoadingMore && !chatLoading" class="chat-load-more-bar"><div class="tg-chat-spinner"></div>加载中…</div>
          <template v-for="(msg, idx) in messages">
            <!-- Unread divider -->
            <div v-if="msg.id && msg.id === currentFirstUnreadId" :key="'unread-' + msg.id" class="unread-divider" :id="'unread-divider-' + msg.id">
              <div class="unread-divider-line"></div>
              <span class="unread-divider-text">以下为未读消息</span>
              <div class="unread-divider-line"></div>
            </div>

            <!-- Day chip -->
            <div v-if="msg.kind === 'day'" :key="'day-' + (msg.id || idx)" class="telegram-day-chip">{{ msg.text }}</div>

            <!-- System notification -->
            <div v-else-if="msg.kind === 'system'" :key="'sys-' + (msg.id || idx)" :id="msg.id ? 'msg-' + msg.id : null" class="telegram-system-row">
              <div class="telegram-system-msg" :class="sysClass(msg.text)">
                <svg v-if="sysClass(msg.text) === 'sys-join'" class="sys-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
                <svg v-else-if="sysClass(msg.text) === 'sys-leave'" class="sys-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/><line x1="17" y1="11" x2="23" y2="11"/></svg>
                <svg v-else-if="sysClass(msg.text) === 'sys-agent'" class="sys-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M12 2a4 4 0 014 4v5H8V6a4 4 0 014-4z"/><line x1="8" y1="16" x2="8" y2="16.01"/><line x1="16" y1="16" x2="16" y2="16.01"/></svg>
                <svg v-else-if="sysClass(msg.text) === 'sys-topic'" class="sys-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                <svg v-else-if="sysClass(msg.text) === 'sys-topic-close'" class="sys-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                <svg v-else class="sys-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                <span v-html="sysHighlight(msg.text)"></span>
              </div>
            </div>

            <!-- Typing row -->
            <div v-else-if="msg.typing" :key="'typing-' + msg.id" class="telegram-row left">
              <div
                class="telegram-message-avatar"
                :style="msgAvatarStyle(msg)"
              ><img v-if="msg.avatarImage" :src="msg.avatarImage" style="width:100%;height:100%;object-fit:cover;border-radius:inherit;" /><template v-else>{{ msg.avatarText || (msg.name || '?').slice(0,1) }}</template></div>
              <div class="telegram-message-stack">
                <div class="telegram-bubble incoming">
                  <div v-if="msg.name" class="telegram-name-line">
                    <div class="telegram-group-name" :style="msg.nameColor ? { color: msg.nameColor } : {}">{{ msg.name }}</div>
                    <span v-if="msg.agentTag" class="telegram-agent-badge" :class="bubbleAgentBadgeClass(msg)">{{ bubbleAgentBadgeText(msg) }}</span>
                  </div>
                  <div class="telegram-typing"><span></span><span></span><span></span></div>
                </div>
              </div>
            </div>

            <!-- Regular message row -->
            <div
              v-else
              :key="msg.id"
              :id="'msg-' + msg.id"
              class="telegram-row is-replyable"
              :class="[msg.side === 'right' ? 'right' : 'left', { 'multi-select-mode': multiSelectMode }]"
              @click="multiSelectMode ? toggleMultiSelect(msg) : null"
            >
              <div v-if="multiSelectMode" class="multi-select-check" :class="{ checked: isMultiSelected(msg.id) }"></div>
              <div
                class="telegram-message-avatar"
                :class="{ self: msg.side === 'right' }"
                :style="msgAvatarStyle(msg)"
                @contextmenu.prevent="msg.side === 'left' && msg.name ? openAvatarMenu($event, msg) : null"
              >{{ msgAvatarText(msg) }}</div>
              <div class="telegram-message-stack">
                <div class="telegram-bubble" :class="[msg.side === 'right' ? 'outgoing' : 'incoming', { 'is-mentioned': msg.isMentioned, 'is-streaming': msg.isStreaming }]" @contextmenu.prevent="openBubbleReplyMenu($event, msg)">
                  <!-- @ mention badge -->
                  <div v-if="msg.isMentioned" class="mention-badge">@</div>
                  <!-- Upload progress overlay -->
                  <div v-if="msg.status === 'sending' && msg.progress !== undefined" class="bubble-upload-progress">
                    <svg viewBox="0 0 40 40" width="40" height="40">
                      <circle cx="20" cy="20" r="16" fill="none" stroke="rgba(255,255,255,0.25)" stroke-width="3.5"/>
                      <circle cx="20" cy="20" r="16" fill="none" stroke="#fff" stroke-width="3.5"
                        stroke-linecap="round"
                        :stroke-dasharray="100.53"
                        :stroke-dashoffset="100.53 * (1 - (msg.progress || 0) / 100)"
                        transform="rotate(-90 20 20)"
                        style="transition: stroke-dashoffset 0.3s ease"
                      />
                    </svg>
                    <span class="bubble-upload-pct">{{ msg.progress || 0 }}%</span>
                  </div>
                  <!-- Name line (left only) -->
                  <div v-if="msg.name && msg.side !== 'right'" class="telegram-name-line">
                    <div class="telegram-group-name" :style="msg.nameColor ? { color: msg.nameColor } : {}">{{ msg.name }}</div>
                    <span v-if="msg.ownerName" class="telegram-owner-name">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="10" height="10"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/></svg>
                      {{ msg.ownerName }}
                    </span>
                    <span v-if="msg.agentTag" class="telegram-agent-badge" :class="bubbleAgentBadgeClass(msg)">{{ bubbleAgentBadgeText(msg) }}</span>
                  </div>
                  <!-- Quote card -->
                  <button v-if="replySource(msg)" class="telegram-quote-card" type="button" @click.stop="jumpToQuote(msg)">
                    <div class="telegram-quote-main">
                      <div class="telegram-quote-name" :style="replySource(msg).nameColor ? { color: replySource(msg).nameColor } : {}">
                        {{ replySource(msg).name }}
                        <span v-if="replySource(msg).agentTag" class="telegram-agent-badge">{{ replySource(msg).agentTag }}</span>
                      </div>
                      <div v-if="replySource(msg).files && replySource(msg).files.length > 1" class="telegram-quote-file">
                        <span class="telegram-quote-file-icon">{{ replyFileIcon(replySource(msg).files[0]) }}</span>
                        <span v-if="replySource(msg).text" class="telegram-quote-file-name">{{ replySource(msg).text }}</span>
                        <span class="telegram-quote-file-count">{{ replySource(msg).files.length }} 个文件</span>
                      </div>
                      <div v-else-if="replySource(msg).files && replySource(msg).files.length === 1" class="telegram-quote-file">
                        <span class="telegram-quote-file-icon">{{ replyFileIcon(replySource(msg).files[0]) }}</span>
                        <span class="telegram-quote-file-name">{{ replySource(msg).text || replySource(msg).files[0].name || '文件' }}</span>
                      </div>
                      <div v-else class="telegram-quote-text">{{ replySource(msg).text || replyTypeFallback(replySource(msg)) }}</div>
                    </div>
                    <img v-if="replySource(msg).files && replySource(msg).files.length && replyFileIsImage(replySource(msg).files[0])" class="telegram-quote-thumb" :src="replySource(msg).files[0].thumbnailUrl || replySource(msg).files[0].url" />
                  </button>
                  <!-- Audio message -->
                  <div v-if="msg.kind === 'audio'" class="tg-audio-wrap">
                    <div class="tg-audio-player" :class="{ 'tg-audio-right': msg.side === 'right' }">
                      <audio
                        :ref="'audio_' + msg.id"
                        :src="msg.fileUrl"
                        preload="none"
                        @timeupdate="onAudioTimeUpdate(msg.id, $event)"
                        @loadedmetadata="onAudioMeta(msg.id, $event)"
                        @ended="onAudioEnded(msg.id)"
                      ></audio>
                      <button class="tg-audio-btn" type="button" @click="toggleAudio(msg.id)">
                        <svg v-if="audioState[msg.id] && audioState[msg.id].playing" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                          <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                        </svg>
                        <svg v-else viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </button>
                      <div class="tg-audio-body">
                        <div class="tg-audio-progress-wrap" @click="seekAudio(msg.id, $event)">
                          <div class="tg-audio-track">
                            <div class="tg-audio-fill" :style="{ width: (audioState[msg.id] && audioState[msg.id].duration ? Math.min(100, audioState[msg.id].current / audioState[msg.id].duration * 100) : 0) + '%' }"></div>
                            <div class="tg-audio-bars">
                              <span v-for="n in 28" :key="n" class="tg-audio-bar" :class="{ active: (n / 28 * 100) <= (audioState[msg.id] && audioState[msg.id].duration ? Math.min(100, audioState[msg.id].current / audioState[msg.id].duration * 100) : 0) }"></span>
                            </div>
                          </div>
                        </div>
                        <div class="tg-audio-meta-row">
                          <span class="tg-audio-time">{{ audioDisplayTime(msg.id) }}</span>
                          <button class="tg-audio-open-ws-btn" type="button" @click.stop="openInWorkspace(msg)">
                            <span class="tg-audio-open-ws-name">{{ msg.fileName || 'Audio' }}</span>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" width="12" height="12"><polyline points="9 18 15 12 9 6"/></svg>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div v-if="msg.text" class="telegram-bubble-text" :class="{ 'md-content': msg.agentTag }" style="margin-top:6px" v-html="msg.html"></div>
                  </div>
                  <!-- Multi-file list (2+ files) - one per row -->
                  <div v-else-if="msg.files && msg.files.length > 1" class="tg-multi-file-wrap">
                    <div class="tg-multi-file-list">
                      <div v-for="(f, fi) in msg.files" :key="f.id || f.url || fi" class="tg-multi-file-item" @click="openFileItem(f, msg, fi)" @contextmenu.prevent.stop="openReplyMenu($event, msg, isImageFile(f) ? f.url : '', isImageFile(f) ? '' : f.url, isImageFile(f) ? '' : (f.name || '文件'))">
                        <!-- image -->
                        <div v-if="f.type === 'image' || (f.mimeType && f.mimeType.startsWith('image/'))" class="tg-mf-img-wrap">
                          <img :src="f.url" class="tg-mf-img" :alt="f.name || '图片'" loading="lazy" />
                        </div>
                        <!-- video -->
                        <div v-else-if="f.type === 'video' || (f.mimeType && f.mimeType.startsWith('video/'))" class="tg-mf-img-wrap tg-mf-video-thumb">
                          <video :src="f.url" class="tg-mf-img" preload="metadata" muted playsinline @loadedmetadata="onVideoThumb"></video>
                          <span class="tg-mf-play">▶</span>
                        </div>
                        <!-- audio -->
                        <div v-else-if="f.type === 'audio' || (f.mimeType && f.mimeType.startsWith('audio/'))" class="tg-mf-doc-icon" style="background:#35B58A">♪</div>
                        <!-- doc/file -->
                        <div v-else class="tg-mf-doc-icon">{{ fileExtLabel(f.name || '') }}</div>
                        <!-- name + size -->
                        <div class="tg-mf-info">
                          <span class="tg-mf-name">{{ f.name || '文件' }}</span>
                          <span class="tg-mf-size">{{ formatSize(f.size) }}</span>
                        </div>
                      </div>
                    </div>
                    <div v-if="msg.text" class="telegram-bubble-text" :class="{ 'md-content': msg.agentTag }" style="margin-top:6px" v-html="msg.html"></div>
                  </div>
                  <!-- Video message -->
                  <div v-else-if="msg.kind === 'video'" class="telegram-video-wrap" @click="openInWorkspace(msg)">
                    <div class="telegram-video-thumb">
                      <video :src="msg.fileUrl" class="telegram-bubble-video" preload="metadata" muted playsinline @loadedmetadata="onVideoThumb"></video>
                      <div class="telegram-video-play">▶</div>
                    </div>
                    <div v-if="msg.text" class="telegram-bubble-text" :class="{ 'md-content': msg.agentTag }" style="margin-top:6px" v-html="msg.html"></div>
                  </div>
                  <!-- Image -->
                  <div v-else-if="msg.kind === 'image'" class="telegram-bubble-image-wrap">
                    <img :src="msg.imageUrl" class="telegram-bubble-image" :alt="msg.fileName || '图片'" :style="imgPlaceholderStyle(msg)" loading="lazy" @load="onImageLoad" @click="openInWorkspace(msg)" />
                    <div v-if="msg.text" class="telegram-bubble-text" :class="{ 'md-content': msg.agentTag }" v-html="msg.html"></div>
                  </div>
                  <!-- File/Document card：只要有文件就渲染为 file card（前面已优先匹配 audio/multi/video/image 分支） -->
                  <div v-else-if="msg.kind === 'document' || msg.kind === 'file' || msg.fileUrl || (msg.files && msg.files.length)" class="telegram-doc-wrap">
                    <button class="telegram-file-card" type="button" @click="openInWorkspace(msg)">
                      <div class="telegram-file-top">
                        <div class="telegram-file-icon" :class="fileIconClass(msg)">{{ fileIconLabel(msg) }}</div>
                        <div class="telegram-file-copy">
                          <div class="telegram-file-title">{{ msg.fileName || msg.title }}</div>
                          <div class="telegram-file-meta">{{ msg.fileSizeStr || msg.meta }}</div>
                        </div>
                        <svg class="telegram-file-open-icon" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7v10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      </div>
                    </button>
                    <div v-if="msg.text" class="telegram-file-caption telegram-bubble-text" :class="{ 'md-content': msg.agentTag }" v-html="msg.html"></div>
                  </div>
                  <!-- Regular text -->
                  <template v-else>
                    <div v-if="msg.isStreaming && msg.thinking && !msg.text" class="telegram-bubble-text" style="color:#94a4b5;font-style:italic;">思考中...</div>
                    <div v-else class="telegram-bubble-text" :class="{ 'md-content': msg.agentTag }" v-html="msg.html"></div>
                    <span v-if="msg.streaming" class="streaming-cursor"></span>
                  </template>
                  <!-- Time + send status -->
                  <div class="telegram-bubble-time">
                    {{ msg.time }}
                    <span v-if="msg.status === 'failed'" class="msg-failed-indicator" @click.stop="retryMessage(msg)" title="点击重试">
                      ⚠️ 发送失败 · 重试
                    </span>
                    <span v-else-if="msg.status === 'sending'" class="msg-sending-indicator">发送中...</span>
                    <span v-else-if="msg.side === 'right' && msg.kind !== 'system'" class="msg-read-status" :class="msg.readCount > 0 ? 'read' : 'unread'" @click.stop="msg.readCount > 0 && showReadReceipt($event, msg)">
                      <svg v-if="msg.readCount > 0" viewBox="0 0 18 12" fill="none"><path d="M1 6l4 4L13 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M6 6l4 4L18 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      <svg v-else viewBox="0 0 14 12" fill="none"><path d="M1 6l4 4L13 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- 跳转到最新消息 -->
        <!-- @ 跳转按钮 -->
        <button v-if="mentionJumpRemaining > 0" class="scroll-to-mention-btn" type="button" @click="jumpToNextMention">
          <span class="mention-jump-badge">{{ mentionJumpRemaining }}</span>
          <svg viewBox="0 0 24 24" fill="none" width="20" height="20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 006 0v-1a10 10 0 10-3.92 7.94"/></svg>
        </button>
        <button v-if="showScrollBtn" class="scroll-to-bottom-btn" type="button" @click="scrollToLatest">
          <svg viewBox="0 0 24 24" fill="none" width="24" height="24"><path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      </div>
      </div>

      <!-- 已读回执浮层 -->
      <div v-if="readReceiptPopup" class="read-receipt-popup" :style="readReceiptPopup.style" @click.stop>
        <div class="read-receipt-title">已读 {{ readReceiptPopup.readCount }} · 未读 {{ readReceiptPopup.unreadCount }}</div>
        <div v-if="readReceiptPopup.readers && readReceiptPopup.readers.length" class="read-receipt-section">
          <div class="read-receipt-section-label">已读</div>
          <div class="read-receipt-list">
            <div v-for="r in readReceiptPopup.readers" :key="r.userId" class="read-receipt-user">
              <div class="read-receipt-avatar">
                <img v-if="r.avatarUrl" :src="r.avatarUrl" />
                <template v-else>{{ (r.name || '?').charAt(0) }}</template>
              </div>
              <span class="read-receipt-name">{{ r.name }}</span>
              <span v-if="r.readAt" class="read-receipt-time">{{ formatReadTime(r.readAt) }}</span>
            </div>
          </div>
        </div>
        <div v-if="readReceiptPopup.unreaders && readReceiptPopup.unreaders.length" class="read-receipt-section">
          <div class="read-receipt-section-label">未读</div>
          <div class="read-receipt-list">
            <div v-for="u in readReceiptPopup.unreaders" :key="u.userId" class="read-receipt-user">
              <div class="read-receipt-avatar">
                <img v-if="u.avatarUrl" :src="u.avatarUrl" />
                <template v-else>{{ (u.name || '?').charAt(0) }}</template>
              </div>
              <span class="read-receipt-name">{{ u.name }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 多选底部工具栏 -->
      <div v-if="multiSelectMode" class="multi-select-bar">
        <button type="button" class="multi-select-action" @click="multiForwardEach" :disabled="!multiSelectedMessages.length">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M15 14l5-5-5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M20 9H9a7 7 0 0 0-7 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
          逐条转发
        </button>
        <button type="button" class="multi-select-action" @click="multiForwardMerge" :disabled="!multiSelectedMessages.length">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/><rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/><rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/><rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/></svg>
          合并转发
        </button>
        <button type="button" class="multi-select-cancel" @click="exitMultiSelect">× 取消</button>
      </div>

      <!-- Attachment compose panel (multi-file, Telegram-style) -->
      <div v-if="pendingAttachments.length" class="tg-attach-overlay">
        <div class="tg-attach-panel">
          <div class="tg-attach-head">
            <span class="tg-attach-title">发送文件
              <span v-if="pendingAttachments.length > 1" class="tg-attach-count">{{ pendingAttachments.length }} 个</span>
            </span>
            <div class="tg-attach-head-actions">
              <button class="tg-attach-add-btn" type="button" title="继续添加文件" @click="triggerFileInput">
                <svg viewBox="0 0 24 24" fill="none" width="18" height="18"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
              </button>
              <button class="tg-attach-close" type="button" @click="cancelAttachment">
                <svg viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
              </button>
            </div>
          </div>
          <!-- multi-file grid -->
          <div class="tg-attach-grid">
            <div v-for="(att, i) in pendingAttachments" :key="i" class="tg-attach-grid-item">
              <img v-if="att.kind === 'image'" :src="att.localUrl" class="tg-attach-grid-img" />
              <video v-else-if="att.kind === 'video'" :src="att.localUrl" class="tg-attach-grid-video" muted playsinline @loadedmetadata="onVideoThumb"></video>
              <div v-else class="tg-attach-grid-doc">
                <div class="tg-attach-grid-doc-icon">{{ fileKindLabel(att) }}</div>
                <div class="tg-attach-grid-doc-name">{{ att.fileName }}</div>
                <div class="tg-attach-grid-doc-size">{{ att.fileSizeStr }}</div>
              </div>
              <!-- 上传进度覆盖层 -->
              <div v-if="attachUploading" class="tg-attach-progress-overlay">
                <div class="tg-attach-progress-ring">
                  <svg viewBox="0 0 36 36" width="36" height="36">
                    <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="3"/>
                    <circle cx="18" cy="18" r="14" fill="none" stroke="#fff" stroke-width="3"
                      stroke-linecap="round"
                      :stroke-dasharray="87.96"
                      :stroke-dashoffset="87.96 * (1 - (uploadProgress[i] || 0) / 100)"
                      transform="rotate(-90 18 18)"
                    />
                  </svg>
                  <span class="tg-attach-progress-pct">{{ uploadProgress[i] || 0 }}%</span>
                </div>
              </div>
              <button v-if="!attachUploading" class="tg-attach-grid-remove" type="button" @click="removeAttachment(i)">
                <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
              </button>
            </div>
          </div>
          <div class="tg-attach-caption-row">
            <textarea
              class="tg-attach-caption"
              placeholder="添加说明（可选）..."
              v-model="attachCaption"
              @keydown.stop="onCaptionKeydown"
              @keyup="checkCaptionMention"
              @input="autoCaptionResize"
              rows="1"
              ref="captionInput"
              :disabled="attachUploading"
            ></textarea>
            <button class="tg-attach-send-btn" :class="{ uploading: attachUploading }" type="button" @click="sendAttachment" :disabled="attachUploading">
              <!-- 上传中显示整体进度环 -->
              <template v-if="attachUploading">
                <svg viewBox="0 0 36 36" width="22" height="22">
                  <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="3"/>
                  <circle cx="18" cy="18" r="14" fill="none" stroke="#fff" stroke-width="3"
                    stroke-linecap="round"
                    :stroke-dasharray="87.96"
                    :stroke-dashoffset="87.96 * (1 - totalUploadProgress / 100)"
                    transform="rotate(-90 18 18)"
                  />
                </svg>
              </template>
              <svg v-else viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M22 2L15 22l-4-9-9-4 18-7z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Compose area -->
      <div v-if="!multiSelectMode" class="telegram-compose">
        <div class="telegram-compose-main">
          <div v-if="pendingReply" class="telegram-reply-draft" :id="'telegram' + stateCap + 'ReplyDraft'">
            <div class="telegram-reply-copy">
              <div class="telegram-reply-name" :id="'telegram' + stateCap + 'ReplyName'">
                {{ pendingReply.name }}
              </div>
              <div v-if="pendingReply.files && pendingReply.files.length > 1" class="telegram-reply-file">
                <span class="telegram-reply-file-icon">{{ replyFileIcon(pendingReply.files[0]) }}</span>
                <span>{{ pendingReply.text ? pendingReply.text + ' · ' : '' }}{{ pendingReply.files.length }} 个文件</span>
              </div>
              <div v-else-if="pendingReply.files && pendingReply.files.length === 1" class="telegram-reply-file">
                <span class="telegram-reply-file-icon">{{ replyFileIcon(pendingReply.files[0]) }}</span>
                <span>{{ pendingReply.text || pendingReply.files[0].name || '文件' }}</span>
              </div>
              <div v-else-if="pendingReply.kind === 'image'" class="telegram-reply-file">
                <span class="telegram-reply-file-icon">🖼️</span>
                <span>{{ pendingReply.text || pendingReply.fileName || '图片' }}</span>
              </div>
              <div v-else-if="pendingReply.kind === 'video'" class="telegram-reply-file">
                <span class="telegram-reply-file-icon">🎬</span>
                <span>{{ pendingReply.text || pendingReply.fileName || '视频' }}</span>
              </div>
              <div v-else-if="pendingReply.kind === 'audio'" class="telegram-reply-file">
                <span class="telegram-reply-file-icon">🎵</span>
                <span>{{ pendingReply.text || pendingReply.fileName || '语音' }}</span>
              </div>
              <div v-else-if="pendingReply.kind === 'document' || pendingReply.kind === 'file'" class="telegram-reply-file">
                <span class="telegram-reply-file-icon">📄</span>
                <span>{{ pendingReply.text || pendingReply.fileName || '文件' }}</span>
              </div>
              <div v-else class="telegram-reply-text" :id="'telegram' + stateCap + 'ReplyText'">
                {{ pendingReply.text || '[消息]' }}
              </div>
            </div>
            <img v-if="pendingReplyThumb" class="telegram-reply-thumb" :src="pendingReplyThumb" />
            <button class="telegram-reply-close" type="button" aria-label="取消引用" @click="clearReply">
              <svg viewBox="0 0 24 24" fill="none"><path d="M8 8l8 8M16 8l-8 8" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"></path></svg>
            </button>
          </div>
          <div class="telegram-compose-tools">
            <button class="tool-btn" type="button" :data-skill-trigger="'telegram-' + state" @click="openSkillPicker">
              <svg viewBox="0 0 24 24" fill="none"><path d="M12 3l2.4 4.86 5.36.78-3.88 3.78.92 5.34L12 15.77 7.2 17.76l.92-5.34L4.24 8.64l5.36-.78L12 3z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path></svg>
              <span class="tool-btn-label">使用技能</span>
            </button>
            <button class="tool-btn composer-model-btn" type="button" :data-model-trigger="'telegram-' + state" @click="openModelPicker">
              <span class="tool-btn-label tool-btn-model-label">{{ selectedModel }}</span>
              <span class="composer-model-btn-caret">▾</span>
            </button>
          </div>
          <div class="composer-feedback-row telegram-compose-feedback" :data-skill-feedback="'telegram-' + state" :hidden="selectedSkills.length === 0">
            <span v-for="skill in selectedSkills" :key="skill.id" class="composer-skill-chip">
              <span class="composer-skill-chip-icon">
                <img v-if="isUrlIcon(skill.icon)" :src="skill.icon" alt="" @error="onIconError" />
                <span v-else>{{ skill.icon }}</span>
              </span>
              <span>{{ skill.name }}</span>
              <button class="composer-skill-chip-remove" type="button" @click="removeSkill(skill.id)">×</button>
            </span>
          </div>
          <div class="telegram-compose-row">
            <div class="telegram-compose-actions left">
              <button class="telegram-compose-btn" type="button" aria-label="发送表情" data-compose-action="emoji" @click.stop="openEmojiMenu">
                <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.8"></circle><path d="M9 10h.01M15 10h.01" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"></path><path d="M8.5 14.5c1.1 1.1 2.25 1.65 3.5 1.65s2.4-.55 3.5-1.65" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>
              </button>
              <button class="telegram-compose-btn" type="button" aria-label="发送文件" data-compose-action="file" @click="triggerFileInput">
                <svg viewBox="0 0 24 24" fill="none"><path d="M7 7.5V17a4 4 0 108 0V6a2.5 2.5 0 10-5 0v10.5a1 1 0 102 0V8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>
              </button>
              <input type="file" ref="fileInput" accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.zip,.rar,.7z" style="display:none" multiple @change="handleFileSelect" />
            </div>
            <div class="telegram-editor">
              <div v-if="voiceActive" class="voice-recording-panel">
                <template v-if="voiceError">
                  <div class="voice-recording-error">{{ voiceError }}</div>
                  <button class="voice-recording-stop" type="button" @click="voiceActive = false; voiceError = ''">关闭</button>
                </template>
                <template v-else>
                  <div class="voice-recording-indicator"><span class="voice-recording-dot"></span><span class="voice-recording-label">正在聆听...</span></div>
                  <div v-if="voiceText" class="voice-recording-result">{{ voiceText }}</div>
                  <button class="voice-recording-stop" type="button" @click="toggleVoice">停止录入</button>
                </template>
              </div>
              <textarea v-else
                :id="'telegram' + stateCap + 'Input'"
                :placeholder="composePlaceholder"
                :value="inputValue"
                @input="onInput"
                @keydown.stop="onKeydown"
                @keyup="onKeyup"
                ref="textarea"
              ></textarea>
            </div>
            <div class="telegram-compose-actions right">
              <!-- <button class="telegram-compose-btn" :class="{ 'voice-active': voiceActive }" type="button" aria-label="语音输入" @click="toggleVoice">
                <svg viewBox="0 0 24 24" fill="none"><path d="M12 3a3 3 0 013 3v6a3 3 0 11-6 0V6a3 3 0 013-3zM19 11a7 7 0 01-14 0M12 18v3M8 21h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>
              </button> -->
              <button class="telegram-send-btn" :id="'telegram' + stateCap + 'Send'" type="button" aria-label="发送" @click="send">
                <svg viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path><path d="M22 2L15 22l-4-9-9-4 18-7z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path></svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 置顶消息面板 -->
      <PinnedMessagesPanel
        :pinned-key="pinnedKey"
        :conversation-id="currentConversationId || ''"
        :topic-id="currentTopicKey || ''"
      />
    </div>

    <!-- Avatar right-click menu -->
    <div v-if="avatarMenu.open" class="telegram-popover-menu reply-menu open" :style="{ left: avatarMenu.x + 'px', top: avatarMenu.y + 'px' }">
      <button class="telegram-popover-item" type="button" @click.stop="doMentionFromAvatar">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M16 8v5a3 3 0 006 0v-1a10 10 0 10-3.92 7.94"/></svg>
        @提及
      </button>
      <button v-if="avatarMenu.memberType !== 'agent' || avatarMenu.isMyAgent" class="telegram-popover-item" type="button" @click.stop="startChatFromAvatar">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        创建会话
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import { api } from '../utils/api'
import { getSpeechUnsupportedReason, startSpeechRecognition, stopSpeechRecognition } from '../utils/speech'
import { msgHtmlCache } from '../utils/lru'
import { renderMarkdown } from '../utils/markdown'
import { solidFromColor } from '../utils/avatarColor'
import { currentStudioHost, currentTableHost } from '../utils/studioUrl'
import PinnedMessageBar from './PinnedMessageBar.vue'
import PinnedMessagesPanel from './PinnedMessagesPanel.vue'

// 需要用 iframe 弹窗打开的域名（而非跳新 tab）
const IFRAME_DOMAINS = [
  'dev-teable.fengbao.com',
  'teable.fengbao.com'
]
// 走 API 网关代理出来的 Teable 记录链接：hostname 跟 API 网关同源，必须靠 pathname 前缀进一步判定，
// 不能整域名白名单，否则会误伤所有 /api/* 调用。
const IFRAME_GATEWAY_PATHS = [
  { hostname: 'arp-public-gateway.fengbao.com',     pathPrefix: '/r/teable-record' },
  { hostname: 'dev-arp-public-gateway.fengbao.com', pathPrefix: '/r/teable-record' }
]
function _shouldOpenInIframe(href) {
  try {
    const u = new URL(href)
    // Teable 域名直接内嵌
    if (IFRAME_DOMAINS.some(d => u.hostname === d)) return true
    // 网关代理出来的 teable-record 链接（按 hostname + path 双匹配）
    if (IFRAME_GATEWAY_PATHS.some(r => u.hostname === r.hostname && u.pathname.startsWith(r.pathPrefix))) return true
    // 本站 /c/{token} 分享链接不拦截，走 ShareAcceptView 路由
    return false
  } catch (_) { return false }
}

function _getImageDimensions(url) {
  return new Promise(resolve => {
    const img = new Image()
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = () => resolve({ width: 0, height: 0 })
    img.src = url
  })
}

function _preloadImage(url) {
  return new Promise(resolve => {
    const img = new Image()
    img.onload = resolve
    img.onerror = resolve
    img.src = url
  })
}

export default {
  name: 'TelegramChat',
  components: { PinnedMessageBar, PinnedMessagesPanel },
  props: {
    state: { type: String, required: true }
  },
  data() {
    return {
      pendingAttachments: [],
      attachCaption: '',
      attachUploading: false,
      showScrollBtn: false,
      unreadJumpDismissed: false,
      unreadJumpSnapshot: 0,
      readReceiptPopup: null,
      isDragOver: false,
      avatarMenu: { open: false, x: 0, y: 0, name: '', agentTag: null },
      voiceActive: false,
      voiceText: '',
      voiceError: '',
      _voiceBaseText: '',
      audioState: {},
      onlineCount: null,
      onlineCountTimer: null,
      streamRenderedLen: 0  // 上次已渲染的 streaming 内容长度
    }
  },
  computed: {
    ...mapState(['currentSessionKey', 'currentTopicKey', 'currentTelegramPrompt', 'currentTelegramSentReply', 'telegramSessionProfiles', 'composerInputValues', 'currentTopicsPageKey', 'sessionTopicPages', 'mentionableMembers', 'auth', 'onlineCountVersion', 'telegramMentionMenu', 'chatLoading', 'hotkeySettings', 'mentionedMsgIds', 'mentionJumpIndex', 'firstUnreadMessageId', 'unreadByConversation', 'multiSelectMode', 'multiSelectedMessages', 'pinnedPanelOpen']),
    ...mapGetters(['selectedModel', 'selectedSkills', 'currentTelegramProfile', 'currentConversationMessages', 'currentConversationId', 'currentAgentTyping', 'currentStreamingMessage', 'currentSession', 'currentTelegramPendingReply']),
    profile() { return this.currentTelegramProfile },
    pinnedKey() {
      const cid = this.currentConversationId
      if (!cid) return ''
      return `${cid}::${this.currentTopicKey || 'default'}`
    },
    isLoadingMore() {
      const cid = this.currentConversationId
      return !!(cid && this.$store.state.conversationMessagesLoading[cid])
    },
    chatHeadTitle() {
      if (!this.profile) return ''
      const sessionName = this.profile.title || ''
      // 当前会话 item，获取 topicKey
      const sessionItem = this.$store.state.sessionItems.find(s => s.key === this.currentSessionKey)
      const topicPageKey = sessionItem?.topicKey || this.currentTopicsPageKey
      const topicPage = topicPageKey ? this.sessionTopicPages?.[topicPageKey] : null
      const activeTopicKey = topicPage?.activeKey
      const activeTopic = activeTopicKey
        ? topicPage?.topics?.find(t => t.key === activeTopicKey)
        : null
      const topicName = activeTopic?.title || activeTopic?.name || activeTopic?.label || ''
      return topicName ? `${sessionName} · ${topicName}` : sessionName
    },
    stateCap() { return this.state.charAt(0).toUpperCase() + this.state.slice(1) },
    messages() {
      // ── 带 LRU 缓存的 HTML 渲染，避免同一条消息重复 escape+regex ──
      const prompt = this.currentTelegramPrompt || this.profile?.defaultRequest || ''
      // 系统/官方会话:后端可控,对方(左侧)消息文本直接当 HTML 渲染(支持 <a>/<b>/<ul> 等)
      const isSystemSession = !!(this.currentSession && this.currentSession.isSystem)
      const _renderHtml = (msg) => {
        if (!msg.text) return ''
        const cacheKey = (msg._cacheKey || msg.id || '') + '|' + (msg.text || '').length + '|' + prompt + (isSystemSession && msg.side === 'left' ? '|sys' : '')
        const cached = msgHtmlCache.get(cacheKey)
        if (cached !== undefined) return cached
        const text = (msg.text || '').replace(/\{\{query\}\}/g, prompt)
        // 系统会话对方消息:直接信任后端 HTML,不做转义/markdown 转换
        if (isSystemSession && msg.side === 'left') {
          msgHtmlCache.set(cacheKey, text)
          return text
        }
        // Agent 消息走 markdown 渲染
        if (msg.side === 'left' && msg.agentTag) {
          const html = renderMarkdown(text)
          msgHtmlCache.set(cacheKey, html)
          return html
        }
        const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        // URL 后可能粘连中英文标点，剥出来当普通文本避免 href 里带逗号 / 句号等
        const withLinks = escaped.replace(
          /(https?:\/\/[^\s<>"'，。！？、）】\u3001\u3002\uff01\uff1f]+)/g,
          (raw) => {
            let u = raw, trail = ''
            while (u.length > 0 && /[.,!?;:，。！？；：、【】…—·]$/.test(u[u.length - 1])) {
              trail = u[u.length - 1] + trail
              u = u.slice(0, -1)
            }
            return `<a href="${u}" target="_blank" rel="noopener noreferrer" class="telegram-link">${u}</a>${trail}`
          }
        )
        // 用 rawText 和 text 对比，精确提取被 @ 的名字并高亮
        const rawText = msg.rawText || ''
        let html = withLinks
        if (rawText && rawText.includes('[@#')) {
          // 从 rawText 提取 [@#n] 的位置，对应 text 里的 @名字
          const mentionNames = []
          let ri = 0, ti = 0
          while (ri < rawText.length && ti < text.length) {
            if (rawText.substring(ri).match(/^\[@#\d+\]/)) {
              const placeholder = rawText.substring(ri).match(/^\[@#\d+\]/)[0]
              ri += placeholder.length
              // text 里对应位置是 @名字
              if (text[ti] === '@') {
                ti++ // 跳过 @
                let name = ''
                while (ti < text.length && rawText[ri] !== text[ti] && !/[\s\n]/.test(text[ti])) {
                  name += text[ti]
                  ti++
                }
                // 如果 rawText 已到末尾，把 text 剩余部分也算入名字（去掉尾部空格）
                if (ri >= rawText.length && !name) {
                  name = text.substring(ti).trim()
                  ti = text.length
                }
                if (name) mentionNames.push(name)
              }
            } else {
              ri++
              ti++
            }
          }
          for (const mName of mentionNames) {
            const esc = mName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            html = html.replace(new RegExp('@' + esc, 'g'),
              `<button class="telegram-mention" type="button" data-mention="@${mName}">@${mName}</button>`)
          }
        }
        // 命令高亮（跳过 @mention 前缀后匹配 /命令）
        const cmdMatch = text.match(/^(?:@\S+\s*)*(\/[\w-]+)/)
        if (cmdMatch) {
          html = html.replace(cmdMatch[1], `<span class="bubble-cmd-tag">${cmdMatch[1]}</span>`)
        }
        if (msg.skills && msg.skills.length) {
          const skillsHtml = msg.skills.map(s => `<span class="bubble-skill-tag">${(s.name || s.title || '').replace(/</g, '&lt;')}</span>`).join(' ')
          html = skillsHtml + ' ' + html
        }
        // 带文件的转发消息：caption 以"转发自"开头，加标签 + 正文分开
        if (msg.kind !== 'text' && text.startsWith('转发自 ')) {
          let fwdText = text, fwdCommentText = ''
          const commentIdx = text.indexOf('\n留言：')
          if (commentIdx >= 0) { fwdCommentText = text.slice(commentIdx + 4); fwdText = text.slice(0, commentIdx) }
          const fwdMatch = fwdText.match(/^(转发自\s+\S+?)(?:[：:](.*))?$/s)
          if (fwdMatch) {
            const label = fwdMatch[1].replace(/&/g, '&amp;').replace(/</g, '&lt;')
            const rest = (fwdMatch[2] || '').trim()
            let bodyHtml = ''
            if (rest) {
              bodyHtml = '<div class="fwd-file-body md-content" style="margin-top:4px">' + renderMarkdown(rest) + '</div>'
            }
            html = '<div class="fwd-file-label"><svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M15 14l5-5-5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M20 9H9a7 7 0 0 0-7 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg> ' + label + '</div>' + bodyHtml
            if (fwdCommentText) html += '<div class="fwd-comment">' + fwdCommentText.replace(/&/g, '&amp;').replace(/</g, '&lt;') + '</div>'
          }
        }
        // 合并转发卡片样式：解析 [img:url] [file:name:url] 标记
        if (text.startsWith('【合并转发】')) {
          const lines = text.replace(/^【合并转发】\n?/, '').split('\n').filter(l => l.trim())
          const linesHtml = lines.map(l => {
            // 先提取 [img:url] 和 [file:name:url]
            let lineHtml = ''
            let rest = l
            // 解析 "发送者：" 前缀
            const nameMatch = rest.match(/^(.+?)[：:]/)
            let nameHtml = ''
            if (nameMatch) {
              nameHtml = '<div class="fwd-merge-name">' + nameMatch[1].replace(/&/g, '&amp;').replace(/</g, '&lt;') + '</div>'
              rest = rest.slice(nameMatch[0].length)
            }
            // 解析 [img:url]
            const imgMatch = rest.match(/^\[img:([^\]]+)\]/)
            let imgHtml = ''
            if (imgMatch) {
              imgHtml = '<img class="fwd-merge-img" src="' + imgMatch[1] + '" loading="lazy" />'
              rest = rest.slice(imgMatch[0].length)
            }
            // 解析 [file:name:size:url]（兼容旧格式 [file:name:url]）
            const fileMatch = rest.match(/^\[file:([^:]*):([^:]*):([^\]]+)\]/) || rest.match(/^\[file:([^:]*):([^\]]+)\]/)
            let fileHtml = ''
            if (fileMatch) {
              const fName = fileMatch[1].replace(/</g, '&lt;')
              let fSize = '', fUrl = ''
              if (fileMatch.length === 4) { fSize = fileMatch[2]; fUrl = fileMatch[3] }
              else { fUrl = fileMatch[2] }
              const fExt = (fName.split('.').pop() || '').toUpperCase()
              const iconClass = { PDF:'tg-icon-pdf', DOC:'tg-icon-doc', DOCX:'tg-icon-doc', XLS:'tg-icon-xls', XLSX:'tg-icon-xls', PPT:'tg-icon-ppt', PPTX:'tg-icon-ppt', ZIP:'tg-icon-zip', RAR:'tg-icon-zip', TXT:'tg-icon-txt', MD:'tg-icon-txt', HTML:'tg-icon-html' }[fExt] || 'tg-icon-default'
              const metaHtml = fSize ? '<div class="telegram-file-meta">' + fSize + '</div>' : ''
              fileHtml = '<a class="fwd-merge-file" href="' + fUrl + '" data-file-url="' + fUrl + '" data-file-name="' + fName + '">' +
                '<div class="telegram-file-top"><div class="telegram-file-icon ' + iconClass + '">' + (fExt || '文') + '</div>' +
                '<div class="telegram-file-copy"><div class="telegram-file-title">' + fName + '</div>' + metaHtml + '</div>' +
                '<svg class="telegram-file-open-icon" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7v10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
                '</div></a>'
              rest = rest.slice(fileMatch[0].length)
            }
            // 剩余文字走 markdown（img/file 标记已在上面剥离，这里只渲染纯文字段）
            let textHtml = ''
            if (rest.trim()) {
              textHtml = '<div class="fwd-merge-text md-content">' + renderMarkdown(rest) + '</div>'
            }
            return '<div class="fwd-merge-line">' + nameHtml + imgHtml + fileHtml + textHtml + '</div>'
          }).join('')
          html = '<div class="fwd-card"><div class="fwd-card-head"><svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/><rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/><rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/><rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" stroke-width="1.6"/></svg> 合并转发</div>' + linesHtml + '</div>'
        }
        // 转发消息卡片样式：仅纯文本消息（图片/文件消息 caption 不走卡片，避免和媒体内容重叠）
        if (msg.kind === 'text' && text.startsWith('【转发消息】')) {
          const fwdMatch = text.match(/^【转发消息】来自\s*(.+?)：([\s\S]*)/)
          if (fwdMatch) {
            const fwdFrom = fwdMatch[1].replace(/&/g, '&amp;').replace(/</g, '&lt;')
            let fwdBody = fwdMatch[2] || ''
            // 留言部分拆出来
            let fwdComment = ''
            const commentIdx = fwdBody.indexOf('\n留言：')
            if (commentIdx >= 0) {
              fwdComment = fwdBody.slice(commentIdx + 4).replace(/&/g, '&amp;').replace(/</g, '&lt;')
              fwdBody = fwdBody.slice(0, commentIdx)
            }
            // body 走 markdown 渲染（转发的可能是 agent 的 markdown；renderMarkdown 自带转义 + 自动链接）
            const bodyMd = renderMarkdown(fwdBody)
            html = `<div class="fwd-card"><div class="fwd-card-head"><svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M15 14l5-5-5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M20 9H9a7 7 0 0 0-7 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg> 转发消息</div><div class="fwd-card-from">来自 ${fwdFrom}</div><div class="fwd-card-body md-content">${bodyMd}</div></div>`
            if (fwdComment) html += `<div class="fwd-comment">${fwdComment}</div>`
          }
        }
        msgHtmlCache.set(cacheKey, html)
        return html
      }
      const _addHtml = (list) => list.map(m => m.html !== undefined ? m : { ...m, html: _renderHtml(m) })

      const _insertDateSeps = (list) => {
        const result = []
        let lastDate = ''
        for (let i = 0; i < list.length; i++) {
          const msg = list[i]
          if (msg.createdAt) {
            const d = new Date(msg.createdAt)
            const dateKey = d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate()
            if (dateKey !== lastDate) {
              lastDate = dateKey
              const now = new Date()
              const today = now.getFullYear() + '-' + (now.getMonth() + 1) + '-' + now.getDate()
              const yesterday = new Date(now - 86400000)
              const yesterdayKey = yesterday.getFullYear() + '-' + (yesterday.getMonth() + 1) + '-' + yesterday.getDate()
              let label
              if (dateKey === today) label = '今天'
              else if (dateKey === yesterdayKey) label = '昨天'
              else if (d.getFullYear() === now.getFullYear()) label = (d.getMonth() + 1) + '月' + d.getDate() + '日'
              else label = d.getFullYear() + '年' + (d.getMonth() + 1) + '月' + d.getDate() + '日'
              result.push({ id: 'day-' + dateKey, kind: 'day', text: label })
            }
          }
          result.push(msg)
        }
        return result
      }

      const _dedup = (list) => {
        const seen = new Set()
        return list.filter(m => {
          if (!m.id) return true
          if (seen.has(m.id)) return false
          seen.add(m.id)
          return true
        })
      }

      // 优先使用 API 真实消息（如果当前会话是 API 会话）
      if (this.currentConversationMessages !== null) {
        const base = _insertDateSeps(_addHtml(_dedup([...this.currentConversationMessages])))
        // 追加流式消息（Agent 正在生成内容）——流式消息不缓存（内容持续变化）
        const streaming = this.currentStreamingMessage
        if (streaming) {
          if (streaming.content) {
            // 有正文内容：用 streamRenderedLen 做打字机效果
            const visibleText = streaming.content.substring(0, this.streamRenderedLen)
            const streamMsg = {
              id: 'streaming-' + streaming.agentId,
              side: 'left',
              name: streaming.agentName || 'Agent',
              agentTag: 'Agent',
              text: visibleText,
              kind: 'text',
              streaming: true,
              avatarText: (streaming.agentName || 'A').slice(0, 1),
              avatarImage: streaming.avatarUrl || null,
              avatarColorRgb: streaming.avatarColorRgb || '',
              html: renderMarkdown(visibleText)
            }
            base.push(streamMsg)
          } else {
            base.push({
              id: 'streaming-' + streaming.agentId,
              typing: true,
              side: 'left',
              name: streaming.agentName || 'Agent',
              agentTag: 'Agent',
              avatarText: (streaming.agentName || 'A').slice(0, 1),
              avatarImage: streaming.avatarUrl || null,
              avatarColorRgb: streaming.avatarColorRgb || ''
            })
          }
        } else {
          // 无流式内容时显示 typing 指示器
          const typing = this.currentAgentTyping
          if (typing) {
            base.push({
              id: 'typing-' + typing.agentId,
              typing: true,
              side: 'left',
              name: typing.agentName || 'Agent',
              avatarText: (typing.agentName || 'A').slice(0, 1),
              avatarImage: typing.avatarUrl || null,
              avatarColorRgb: typing.avatarColorRgb || ''
            })
          }
        }
        return base
      }
      // 降级：使用 mock 数据
      const profile = this.profile
      if (!profile) return []
      if (this.currentTopicsPageKey && profile.topicStates) {
        const activeTopic = this.sessionTopicPages?.[this.currentTopicsPageKey]?.activeKey
        if (activeTopic && profile.topicStates[activeTopic]?.[this.state]) {
          return _addHtml(profile.topicStates[activeTopic][this.state])
        }
      }
      return _addHtml(profile.states?.[this.state] || [])
    },
    avatarHeadClass() {
      if (!this.profile) return {}
      return {
        'group-chat': this.profile.type === 'group' && this.profile.groupAvatarLabels?.length,
        'has-image': !!(this.profile.avatarImage)
      }
    },
    avatarHeadStyle() {
      if (!this.profile) return {}
      if (this.profile.type === 'group' && this.profile.groupAvatarLabels?.length) {
        return { background: 'rgba(255,255,255,0.98)' }
      }
      if (this.profile.avatarImage) {
        return { background: `center / cover no-repeat url(${this.profile.avatarImage})` }
      }
      const bg = solidFromColor((this.currentSession && this.currentSession.avatarColorRgb) || this.profile.avatarColorRgb)
      return bg ? { background: bg } : {}
    },
    avatarHeadHtml() {
      if (!this.profile) return ''
      if (this.profile.type === 'group' && this.profile.groupAvatarLabels?.length) {
        const bg = solidFromColor((this.currentSession && this.currentSession.avatarColorRgb) || this.profile.avatarColorRgb)
        const labels = this.profile.groupAvatarLabels.slice(0, 9)
        const nodes = labels.map((l) =>
          `<span class="group-node" style="background:${bg}">${String(l).slice(0,1)}</span>`
        ).join('')
        return `<span class="group-nine-grid">${nodes}</span>`
      }
      if (this.profile.avatarImage) return ''
      return this.profile.avatarText || (this.profile.title || '').slice(0, 1)
    },
    mentionJumpRemaining() {
      const convId = this.currentConversationId
      if (!convId) return 0
      const ids = this.mentionedMsgIds[convId] || []
      const idx = this.mentionJumpIndex[convId] || 0
      return Math.max(0, ids.length - idx)
    },
    currentFirstUnreadId() {
      const convId = this.currentConversationId
      if (!convId) return null
      const topicKey = this.currentTopicKey
      const key = topicKey ? convId + ':' + topicKey : convId
      return this.firstUnreadMessageId[key] || null
    },
    unreadJumpCount() {
      return this.unreadJumpSnapshot || 0
    },
    unreadJumpVisible() {
      return this.unreadJumpCount > 0 && this.currentFirstUnreadId && !this.unreadJumpDismissed
    },
    pendingReplyThumb() {
      const r = this.pendingReply
      if (!r) return ''
      // 优先从 files 取图片缩略图
      if (r.files && r.files.length) {
        const img = r.files.find(f => f.type === 'image' || (f.mimeType && f.mimeType.startsWith('image/')))
        if (img) return img.thumbnailUrl || img.url || ''
      }
      // fallback: kind === image 用 imageUrl
      if (r.kind === 'image' && r.imageUrl) return r.imageUrl
      return ''
    },
    inputValue() {
      return this.composerInputValues[this.composerContext] || ''
    },
    composerContext() {
      // 每个会话 + 每个话题独立保存草稿
      // 优先用 store.currentTopicKey（跟随会话切换的当前 topic），退化到 sessionTopicPages.activeKey
      const sessionKey = this.currentSessionKey || 'unknown'
      const topicKey = this.currentTopicKey || (this.sessionTopicPages?.[this.currentTopicsPageKey]?.activeKey)
      return topicKey
        ? (sessionKey + '-' + this.state + '-' + topicKey)
        : (sessionKey + '-' + this.state)
    },
    pendingReply() { return this.currentTelegramPendingReply },
    groupMembersList() {
      // 优先使用 API 返回的可@成员列表
      const convId = this.currentConversationId
      const apiMembers = convId ? (this.mentionableMembers[convId] || []) : []
      if (apiMembers.length) {
        return apiMembers.map((mb) => {
          const name = mb.name || mb.displayName || mb.display_name || '成员'
          const type = mb.type || mb.senderType || 'user'
          const isAgent = type === 'agent'
          return {
            id: mb.id || mb.userId || mb.user_id,
            type,
            name,
            avatarUrl: mb.avatarUrl || null,
            avatarText: String(name || '?').slice(0, 1),
            // 无图用后端 avatarColorRgb 纯色（不前端生成色）
            avatarBackground: solidFromColor(mb.avatarColorRgb),
            sub: isAgent ? ('负责人：' + (mb.ownerName || mb.owner_name || '—')) : '群成员'
          }
        })
      }
      // 降级使用本地 profile 数据（无后端色，不前端兜底生成）
      const profile = this.profile
      if (!profile) return []
      const members = (this.currentTopicsPageKey && profile.topicMembers) ? profile.topicMembers : (profile.members || [])
      return members.map((name) => {
        const isAgent = /Agent|工程师|产品经理/.test(name)
        return {
          id: name,
          type: isAgent ? 'agent' : 'user',
          name,
          avatarText: String(name).slice(0, 1),
          avatarBackground: '',
          sub: isAgent ? 'AI 助手' : '群成员'
        }
      })
    },
    composePlaceholder() {
      if (!this.profile) return '发送消息'
      return this.profile.type === 'group' ? `发送消息到 ${this.profile.title}` : `发送消息给 ${this.profile.title}`
    },
    // 专门用于监听 caption context 变化，避免对整个 composerInputValues 做 deep watch
    captionContextValue() {
      const captionCtx = 'telegram-caption-' + this.state
      return this.composerInputValues[captionCtx] || ''
    },
    attachIconClass() {
      const first = this.pendingAttachments[0]
      if (!first) return 'tg-attach-icon-default'
      const ext = (first.fileName || '').split('.').pop().toLowerCase()
      if (ext === 'pdf') return 'tg-attach-icon-pdf'
      if (ext === 'doc' || ext === 'docx') return 'tg-attach-icon-doc'
      if (ext === 'xls' || ext === 'xlsx') return 'tg-attach-icon-doc'
      if (ext === 'ppt' || ext === 'pptx') return 'tg-attach-icon-doc'
      if (ext === 'zip' || ext === 'rar' || ext === '7z') return 'tg-attach-icon-zip'
      return 'tg-attach-icon-default'
    },
    attachIconLabel() {
      const first = this.pendingAttachments[0]
      if (!first) return '文'
      const ext = (first.fileName || '').split('.').pop().toLowerCase()
      if (ext === 'pdf') return 'PDF'
      if (ext === 'doc' || ext === 'docx') return 'DOC'
      if (ext === 'xls' || ext === 'xlsx') return 'XLS'
      if (ext === 'ppt' || ext === 'pptx') return 'PPT'
      if (ext === 'zip' || ext === 'rar' || ext === '7z') return 'ZIP'
      if (ext === 'txt') return 'TXT'
      if (ext === 'md') return 'MD'
      return '文'
    }
  },
  methods: {
    // 拦截气泡内 <a> 点击:
    //   命中"当前环境的 studio host" → 跳 /dev?url=,DevView iframe 加载
    //   命中"当前环境的 teable host" → 跳 /collab/table/__direct_<ts>?url=,TableViewer iframe 加载
    //   其它(跨环境 studio / 外部链接) → 不拦,a 标签按浏览器默认(新标签)
    onShellClickCapture(e) {
      const a = e.target && e.target.closest ? e.target.closest('a[href]') : null
      if (!a) return
      const href = a.getAttribute('href') || ''
      if (!href || href.startsWith('#') || href.startsWith('javascript:')) return
      let u
      try { u = new URL(href, location.href) } catch (_) { return }
      if (u.host === currentStudioHost()) {
        e.preventDefault(); e.stopPropagation()
        const cur = this.$route && this.$route.query && this.$route.query.url
        if (this.$route && this.$route.name === 'dev' && cur === u.href) return  // 已在该页
        this.$router.push({ path: '/dev', query: { url: u.href } }).catch(() => {})
        return
      }
      if (u.host === currentTableHost()) {
        e.preventDefault(); e.stopPropagation()
        // TableViewer 约定:id 以 __direct_ 开头 → 跳过文档解析,直接用 query.url 当 iframe src
        const synthId = '__direct_' + Date.now()
        this.$router.push({ name: 'table-viewer', params: { id: synthId }, query: { url: u.href } }).catch(() => {})
        return
      }
      // 其它域名 → 不拦,默认行为
    },
    // 气泡 Agent 徽标: 查这条消息对应 agent 在当前会话 conversationAgents 里的 status, disabled→"已停用"灰; 其它→"Agent"
    bubbleAgentBadgeText(msg) {
      if (!msg || !msg.agentTag) return ''
      const convId = this.currentConversationId
      const agents = (convId && this.$store.state.conversationAgents[convId]) || []
      const found = msg.agentId ? agents.find(a => a.id === msg.agentId) : null
      return found && found.status === 'disabled' ? '已停用' : msg.agentTag
    },
    bubbleAgentBadgeClass(msg) {
      if (!msg || !msg.agentTag) return ''
      const convId = this.currentConversationId
      const agents = (convId && this.$store.state.conversationAgents[convId]) || []
      const found = msg.agentId ? agents.find(a => a.id === msg.agentId) : null
      return found && found.status === 'disabled' ? 'disabled' : ''
    },
    openPinnedPanel() {
      this.$store.commit('SET_PINNED_PANEL_OPEN', true)
    },
    dismissPinnedBar() {
      const key = this.pinnedKey
      if (key) this.$store.commit('SET_PINNED_BAR_DISMISSED', { key, dismissed: true })
    },
    async _fetchOnlineCount(conversationId) {
      try {
        const data = await api.getOnlineCount(conversationId)
        this.onlineCount = data && data.totalOnlineCount != null
          ? data.totalOnlineCount
          : (data && data.data && data.data.totalOnlineCount != null ? data.data.totalOnlineCount : null)
      } catch (e) {
        // 静默失败，保留上一次的值
      }
    },
    _stopOnlineCountTimer() {
      if (this.onlineCountTimer) {
        clearInterval(this.onlineCountTimer)
        this.onlineCountTimer = null
      }
    },
    // Audio Player methods
    _getAudio(id) {
      const r = this.$refs['audio_' + id]
      if (!r) return null
      // In v-for, Vue 2 collects refs as arrays
      return Array.isArray(r) ? r[0] : r
    },
    _ensureAudioState(id) {
      if (!this.audioState[id]) {
        this.$set(this.audioState, id, { playing: false, current: 0, duration: 0 })
      }
    },
    toggleAudio(id) {
      this._ensureAudioState(id)
      const el = this._getAudio(id)
      if (!el) return
      Object.keys(this.audioState).forEach(k => {
        if (k !== String(id) && this.audioState[k] && this.audioState[k].playing) {
          const other = this._getAudio(k)
          if (other) other.pause()
          this.$set(this.audioState[k], 'playing', false)
        }
      })
      if (el.paused) {
        el.play().then(() => {
          this.$set(this.audioState[id], 'playing', true)
        }).catch(err => {
          console.warn('[audio] play() failed:', err.message)
          this.$set(this.audioState[id], 'playing', false)
        })
      } else {
        el.pause()
        this.$set(this.audioState[id], 'playing', false)
      }
    },
    onAudioTimeUpdate(id, e) {
      this._ensureAudioState(id)
      this.$set(this.audioState[id], 'current', e.target.currentTime)
      this.$set(this.audioState[id], 'duration', e.target.duration || 0)
    },
    onAudioMeta(id, e) {
      this._ensureAudioState(id)
      this.$set(this.audioState[id], 'duration', e.target.duration || 0)
    },
    onAudioEnded(id) {
      this._ensureAudioState(id)
      this.$set(this.audioState[id], 'playing', false)
      this.$set(this.audioState[id], 'current', 0)
      const el = this._getAudio(id)
      if (el) el.currentTime = 0
    },
    seekAudio(id, e) {
      this._ensureAudioState(id)
      const el = this._getAudio(id)
      if (!el) return
      const rect = e.currentTarget.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      const dur = el.duration || this.audioState[id].duration || 0
      if (dur) el.currentTime = ratio * dur
    },
    audioProgress(id) {
      const s = this.audioState[id]
      if (!s || !s.duration) return 0
      return Math.min(100, (s.current / s.duration) * 100)
    },
    audioDisplayTime(id) {
      const s = this.audioState[id]
      const fmt = t => {
        if (!t || isNaN(t)) return '0:00'
        const m = Math.floor(t / 60)
        const sec = Math.floor(t % 60)
        return m + ':' + String(sec).padStart(2, '0')
      }
      if (!s) return '0:00'
      if (s.playing || s.current > 0) return fmt(s.current)
      return fmt(s.duration)
    },
    ...mapMutations(['SET_COMPOSER_INPUT', 'CLEAR_SKILLS', 'SET_TELEGRAM_PENDING_REPLY', 'SET_TELEGRAM_GROUP_MENU', 'SET_TELEGRAM_REPLY_MENU', 'SET_TELEGRAM_EMOJI_MENU', 'SET_TELEGRAM_MENTION_MENU', 'CLOSE_TELEGRAM_MENUS', 'SET_WORKSPACE_FILE', 'SET_WORKSPACE_SCENE', 'ADD_PENDING_MENTION']),
    // skill.icon 后端要么 URL（http/https/data/blob/相对路径 /），要么 emoji/单字
    isUrlIcon(s) { return typeof s === 'string' && /^(https?:|data:|blob:|\/)/.test(s) },
    onIconError(e) { try { e.target.style.display = 'none' } catch (_) {} },
    ...mapActions(['startFakeFlow', 'resendMessage']),
    resolveText(text) {
      const prompt = this.currentTelegramPrompt || this.profile?.defaultRequest || ''
      return (text || '').replace(/\{\{query\}\}/g, prompt)
    },
    renderText(msg) {
      const text = this.resolveText(msg.text || '')
      // Escape HTML then convert @mentions to clickable buttons
      const escaped = text.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
      return escaped.replace(/@([\u4e00-\u9fa5A-Za-z0-9\-_]+)/g,
        (_, name) => `<button class="telegram-mention" type="button" data-mention="@${name}">@${name}</button>`)
    },
    _onQuickTask(e) {
      const btn = e.target.closest('.qt-card')
      if (!btn) return
      const action = btn.dataset.qtAction || ''
      if (!action || !this.currentConversationId) return
      const row = btn.closest('.qt-row')
      if (row) row.querySelectorAll('.qt-card').forEach(function (c) { c.classList.remove('qt-selected') })
      btn.classList.add('qt-selected')
      this.$store.dispatch('sendConversationMessage', { conversationId: this.currentConversationId, text: action })
    },
    handleBodyClick(e) {
      const fwdImg = e.target.closest('.fwd-merge-img')
      if (fwdImg && fwdImg.src) {
        e.preventDefault()
        e.stopPropagation()
        const ext = (fwdImg.src.split('.').pop() || 'png').split('?')[0].toLowerCase()
        const previewItem = {
          id: 'fwd-img-preview', previewMode: 'file', title: '图片预览',
          url: fwdImg.src, kind: 'image', fileName: '', mimeType: 'image/*', fileSizeStr: '', ext
        }
        previewItem.autoFullscreen = true
        this.$store.commit('SET_GROUP_WORKSPACE_PREVIEW', previewItem)
        this.$store.commit('SET_GROUP_WORKSPACE_TAB', 'preview')
        this.$store.commit('SET_WORKSPACE_OPEN', true)
        this.$store.commit('SET_FULLSCREEN_PREVIEW', previewItem)
        return
      }
      const fwdFile = e.target.closest('.fwd-merge-file')
      if (fwdFile && (fwdFile.href || fwdFile.dataset.fileUrl)) {
        e.preventDefault()
        e.stopPropagation()
        const url = fwdFile.dataset.fileUrl || fwdFile.href
        const fileName = fwdFile.dataset.fileName || '文件'
        const ext = (fileName.split('.').pop() || '').toLowerCase()
        const previewItem = {
          id: 'fwd-file-preview', previewMode: 'file', title: fileName,
          url, kind: 'file', fileName, mimeType: '', fileSizeStr: '', ext
        }
        this.$store.commit('SET_GROUP_WORKSPACE_PREVIEW', previewItem)
        this.$store.commit('SET_GROUP_WORKSPACE_TAB', 'preview')
        this.$store.commit('SET_WORKSPACE_OPEN', true)
        return
      }
      // 拦截链接
      const link = e.target.closest('a')
      if (link && link.href) {
        try {
          const u = new URL(link.href)
          // Teable 直链 + 网关代理 teable-record 链接：跳 table-viewer 路由页（判断收口到 _shouldOpenInIframe，规则只在常量里改）
          if (_shouldOpenInIframe(link.href)) {
            e.preventDefault()
            e.stopPropagation()
            const directId = '__direct_' + link.href.replace(/\W/g, '').slice(-16) + '__'
            const tableRoute = { name: 'table-viewer', params: { id: directId }, query: { url: link.href } }
            // 用后端去重后返回的记录(resourceId 稳定，不受 URL 归一化影响)驱动最近列表高亮
            api.recordTableRecent(link.href).then(rec => { window.dispatchEvent(new CustomEvent('refresh-recent-files', { detail: { activeDocId: (rec && rec.resourceId) || '', activeUrl: (rec && rec.url) || link.href } })) }).catch(e => { console.error('[TelegramChat] recordTableRecent fail', e) })
            this.$router.push(tableRoute).catch(() => {})
            return
          }
          // 本站分享链接 /#/c/{token}：走当前页面路由，不开新 tab
          if (u.hostname === location.hostname && /^#\/c\//.test(u.hash)) {
            e.preventDefault()
            e.stopPropagation()
            const path = u.hash.replace(/^#/, '')
            this.$router.push(path).catch(() => {})
            return
          }
          // 知识库分享链接：/#/knowledge?wiki_share={key} （新版）或 /#/wiki-share/{key}（兼容旧版）
          if (u.hostname === location.hostname && (/^#\/knowledge\?.*wiki_share=/.test(u.hash) || /^#\/wiki-share\//.test(u.hash))) {
            e.preventDefault()
            e.stopPropagation()
            const path = u.hash.replace(/^#/, '')
            this.$router.push(path).catch(() => {})
            return
          }
        } catch (_) { /* ignore */ }
      }
      const mentionBtn = e.target.closest('.telegram-mention')
      if (!mentionBtn || !mentionBtn.dataset.mention) return
      e.preventDefault()
      e.stopPropagation()
      const mentionText = mentionBtn.dataset.mention // e.g. "@name"
      const name = mentionText.startsWith('@') ? mentionText.slice(1) : mentionText
      // 查找成员，记录 pending mention，输入框显示 @name
      const member = this.groupMembersList.find(m => m.name === name)
      if (member) {
        this.ADD_PENDING_MENTION({ name, mention: { id: member.id, type: member.type || 'user', name: member.name } })
      }
      const displayText = `@${name} `
      const context = this.composerContext
      const val = this.inputValue
      const newVal = val ? (val + ' ' + displayText) : displayText
      this.SET_COMPOSER_INPUT({ context, value: newVal })
      this.$nextTick(() => {
        const ta = this.$refs.textarea
        if (ta) { ta.focus(); ta.setSelectionRange(newVal.length, newVal.length) }
      })
    },
    msgAvatarStyle(msg) {
      if (msg.side === 'right') {
        const url = this.auth && this.auth.avatarUrl
        if (url) return { background: `center / cover no-repeat url(${url})` }
        const bg = solidFromColor(this.auth && this.auth.avatarColorRgb)
        return bg ? { background: bg } : {}
      }
      if (msg.avatarImage) {
        return { background: `center / cover no-repeat url(${msg.avatarImage})` }
      }
      const bg = solidFromColor(msg.avatarColorRgb)
      return bg ? { background: bg } : {}
    },
    msgAvatarText(msg) {
      if (msg.side === 'right') {
        if (this.auth && this.auth.avatarUrl) return ''
        return (this.auth && this.auth.name) ? this.auth.name.slice(0, 1) : (this.profile?.selfAvatarText || 'Me')
      }
      if (msg.avatarImage) return ''
      return msg.avatarText || (msg.name || '?').slice(0, 1)
    },
    openAvatarMenu(e, msg) {
      if (this.profile && this.profile.type !== 'group') return
      // 从可@成员列表查找 id
      const convId = this.currentConversationId
      const members = (convId && this.mentionableMembers[convId]) || []
      const member = members.find(m => m.name === msg.name)
      const memberId = member ? (member.id || member.userId) : null
      const memberType = member ? member.type : (msg.agentTag ? 'agent' : 'user')
      const ownerId = member ? (member.ownerId || member.owner_id || '') : ''
      const isMyAgent = memberType === 'agent' && ownerId === this.$store.state.currentUserId
      this.avatarMenu = { open: true, x: e.clientX, y: e.clientY, name: msg.name, agentTag: msg.agentTag, memberId, memberType, isMyAgent }
      const close = () => { this.avatarMenu.open = false; document.removeEventListener('click', close) }
      setTimeout(() => document.addEventListener('click', close), 0)
    },
    doMentionFromAvatar() {
      const name = this.avatarMenu.name
      if (!name) return
      this.avatarMenu.open = false
      // 在输入框插入 @name
      const val = this.inputValue || ''
      const newVal = val + (val && !val.endsWith(' ') ? ' ' : '') + '@' + name + ' '
      this.SET_COMPOSER_INPUT({ context: this.composerContext, value: newVal })
      // 添加到 pendingMentions
      const convId = this.currentConversationId
      const members = (convId && this.mentionableMembers[convId]) || []
      const member = members.find(m => m.name === name)
      if (member) {
        this.ADD_PENDING_MENTION({ name, mention: { id: member.id, type: member.type || 'user', name: member.name } })
      }
      this.$nextTick(() => {
        const ta = this.$refs.textarea
        if (ta) { ta.focus(); ta.selectionStart = ta.selectionEnd = ta.value.length }
      })
    },
    sysClass(text) {
      if (!text) return ''
      // 注意先匹配更具体的(智能体/话题)再匹配通用的(加入/离开),防误判
      if (/智能体|Agent/.test(text)) return 'sys-agent'
      if (/关闭了话题/.test(text)) return 'sys-topic-close'
      if (/将话题名称修改为|话题/.test(text)) return 'sys-topic'
      if (/离开|退出|移出了/.test(text)) return 'sys-leave'
      if (/加入|邀请/.test(text)) return 'sys-join'
      return ''
    },
    sysHighlight(text) {
      if (!text) return ''
      const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      // 匹配常见系统消息格式，把人名/Agent名/话题名高亮
      return escaped
        .replace(/^(.+?)\s+(邀请)\s+(.+?)\s+(加入了群聊)$/, '<span class="sys-name">$1</span> $2 <span class="sys-name">$3</span> $4')
        .replace(/^(.+?)\s+(离开了群聊)$/, '<span class="sys-name">$1</span> $2')
        .replace(/^(.+?)\s+(将)\s+(.+?)\s+(移出了群聊)$/, '<span class="sys-name">$1</span> $2 <span class="sys-name">$3</span> $4')
        .replace(/^(.+?)\s+(添加了智能体「)(.+?)(」.*)$/, '<span class="sys-name">$1</span> $2<span class="sys-name">$3</span>$4')
        .replace(/^(.+?)\s+(将智能体「)(.+?)(」.*)$/, '<span class="sys-name">$1</span> $2<span class="sys-name">$3</span>$4')
        // 改话题名:「{操作者} 将话题名称修改为 {新话题名}」
        .replace(/^(.+?)\s+(将话题名称修改为)\s+(.+)$/, '<span class="sys-name">$1</span> $2 <span class="sys-name">$3</span>')
        // 关闭话题:「{操作者} 关闭了话题 {话题名}」
        .replace(/^(.+?)\s+(关闭了话题)\s+(.+)$/, '<span class="sys-name">$1</span> $2 <span class="sys-name">$3</span>')
    },
    async startChatFromAvatar() {
      const { memberId, memberType, name } = this.avatarMenu
      this.avatarMenu.open = false
      if (!memberId) return
      try {
        if (memberType === 'agent') {
          // Agent: 创建 agent 私聊会话
          await this.$store.dispatch('createDirectSession', { participantId: memberId })
        } else {
          // 用户: 创建私聊会话
          await this.$store.dispatch('createDirectSession', { participantId: memberId })
        }
      } catch (e) {
        console.warn('[TelegramChat] startChatFromAvatar failed:', e.message)
      }
    },
    openMemberManagerFromHead() {
      window.dispatchEvent(new CustomEvent('open-member-manager'))
    },
    replyFileIsImage(file) {
      if (!file) return false
      const t = file.type || ''
      const mime = file.mimeType || ''
      return t === 'image' || mime.startsWith('image/')
    },
    replyFileIcon(file) {
      if (!file) return '📎'
      const t = file.type || ''
      const mime = file.mimeType || ''
      if (t === 'image' || mime.startsWith('image/')) return '🖼️'
      if (t === 'video' || mime.startsWith('video/')) return '🎬'
      if (t === 'audio' || mime.startsWith('audio/')) return '🎵'
      if (t === 'document' || t === 'markdown') return '📄'
      return '📎'
    },
    replyTypeFallback(reply) {
      if (!reply) return ''
      const t = reply.messageType || ''
      if (t === 'file') return '[文件]'
      if (t === 'image') return '[图片]'
      if (t === 'video') return '[视频]'
      if (t === 'audio') return '[语音]'
      if (t === 'mixed') return '[消息]'
      return '[消息]'
    },
    replySource(msg) {
      if (msg.replyCurrent) return this.currentTelegramSentReply
      return msg.replyTo || null
    },
    onInput(e) {
      this.SET_COMPOSER_INPUT({ context: this.composerContext, value: e.target.value })
      this.autoResize()
      this.$nextTick(() => this.checkMention())
    },
    onKeydown(e) {
      // 输入法组合状态（如中文拼音输入中）不拦截
      if (e.isComposing || e.keyCode === 229) return
      if (e.key === 'Escape') { this.CLOSE_TELEGRAM_MENUS(); return }
      // @ 列表打开时，上下键切换选中，Enter/Tab 选中当前项
      if (this.telegramMentionMenu && this.telegramMentionMenu.open && this.telegramMentionMenu.members.length) {
        const members = this.telegramMentionMenu.members
        const cur = this.$store.state.mentionActiveIndex
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          this.$store.commit('SET_MENTION_ACTIVE_INDEX', (cur + 1) % members.length)
          return
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          this.$store.commit('SET_MENTION_ACTIVE_INDEX', (cur - 1 + members.length) % members.length)
          return
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault()
          const m = members[cur]
          if (m) this.insertMentionByName(m.name)
          return
        }
      }
      if (this._matchHotkey(e, this.hotkeySettings.send)) {
        e.preventDefault()
        this.send()
      } else if (this._matchHotkey(e, this.hotkeySettings.newline)) {
        e.preventDefault()
        const ta = e.target
        ta.setRangeText('\n', ta.selectionStart, ta.selectionEnd, 'end')
        this.SET_COMPOSER_INPUT({ context: this.composerContext, value: ta.value })
        this.$nextTick(() => { this.autoResize(); ta.scrollTop = ta.scrollHeight })
      } else if (e.key === 'Enter') {
        e.preventDefault()
      }
    },
    _matchHotkey(e, hk) {
      if (!hk || !hk.key) return false
      return e.key === hk.key && !!e.ctrlKey === !!hk.ctrl && !!e.shiftKey === !!hk.shift && !!e.altKey === !!hk.alt
    },
    insertMentionByName(name) {
      const { context, atStart, query, val, members, textareaId } = this.telegramMentionMenu
      const member = members.find(m => m.name === name)
      const displayText = `@${name} `
      const newVal = val.slice(0, atStart) + displayText + val.slice(atStart + 1 + query.length)
      this.SET_COMPOSER_INPUT({ context, value: newVal })
      if (member) {
        this.ADD_PENDING_MENTION({ name, mention: { id: member.id, type: member.type || 'user', name: member.name } })
      }
      this.CLOSE_TELEGRAM_MENUS()
      this.$nextTick(() => {
        const ta = textareaId ? document.getElementById(textareaId) : this.$refs.captionInput
        if (ta) { ta.focus(); const pos = atStart + displayText.length; ta.setSelectionRange(pos, pos) }
      })
    },
    send() {
      const val = this.inputValue.trim()
      if (!val) return
      this.SET_COMPOSER_INPUT({ context: this.composerContext, value: '' })
      this.$nextTick(() => this.autoResize())

      // 发送后强制滚到底部
      const key = this._scrollKey()
      if (key) this._scrollAtBottom[key] = true
      this._scrollToBottom()

      // 引用回复
      const reply = this.pendingReply
      const replyToId = reply ? reply.messageId : undefined
      if (reply) this.clearReply()

      // 如果是 API 真实会话，调用真实接口发送
      if (this.currentConversationId) {
        this.$store.dispatch('sendConversationMessage', {
          conversationId: this.currentConversationId,
          text: val,
          replyToId,
          replyTo: reply ? { id: reply.messageId, name: reply.name || '', text: reply.text || '', files: reply.files || null } : undefined
        })
        // 会话列表滚到顶部
        this.$nextTick(() => { const el = document.getElementById('sessionList'); if (el) el.scrollTop = 0 })
      } else {
        // 降级：使用 mock 流程
        this.startFakeFlow(val || this.profile?.defaultRequest || '把最新进展同步一下')
      }
    },
    _getTopicAwareUnread(convId) {
      const topicKey = this.$store.state.currentTopicKey
      if (topicKey && convId) {
        const si = this.$store.state.sessionItems.find(s => s.conversationId === convId)
        if (si && si.topicKey) {
          const page = this.$store.state.sessionTopicPages[si.topicKey]
          const topic = page && page.topics.find(t => t.key === topicKey)
          if (topic && topic.unread) return topic.unread
        }
      }
      const sessionItem = convId ? this.$store.state.sessionItems.find(s => s.conversationId === convId) : null
      return (sessionItem && sessionItem.unread) || (convId ? (this.unreadByConversation[convId] || 0) : 0)
    },
    autoResize() {
      const ta = this.$refs.textarea
      if (!ta) return
      const lh = parseFloat(window.getComputedStyle(ta).lineHeight) || 24
      ta.style.height = 'auto'
      ta.style.height = Math.min(Math.max(ta.scrollHeight, lh), lh * 8) + 'px'
    },
    async jumpToQuote(msg) {
      const reply = msg.replyTo
      if (!reply || !reply.id) return
      const convId = this.currentConversationId
      if (!convId) return
      const topicId = this.$store.state.currentTopicKey || undefined
      const found = await this.$store.dispatch('jumpToMessage', { conversationId: convId, topicId, messageId: reply.id })
      if (!found) return
      // 等 Vue 渲染 + 浏览器布局 + 多等一帧给图片占位完成布局
      await this.$nextTick()
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)))
      const el = document.getElementById('msg-' + reply.id)
      if (!el) return
      // 不用 smooth：合并后容器高度还可能变化，smooth 动画期间位置会跑偏
      el.scrollIntoView({ block: 'center' })
      el.classList.add('highlight-flash')
      setTimeout(() => el.classList.remove('highlight-flash'), 2000)
    },
    clearReply() { this.SET_TELEGRAM_PENDING_REPLY(null) },
    positionMenu(rect, width, height, forceAbove) {
      const vw = window.innerWidth
      const vh = window.innerHeight
      let left = Math.min(rect.left, vw - width - 16)
      left = Math.max(16, left)
      const openAbove = forceAbove || (rect.bottom + height + 8 > vh - 12)
      const top = openAbove ? rect.top - height - 8 : rect.bottom + 8
      return { x: left, y: Math.max(12, Math.min(top, vh - height - 12)) }
    },
    toggleWorkspace() {
      this.$store.commit('SET_WORKSPACE_OPEN', !this.$store.state.workspaceOpen)
    },
    toggleChatSearch() {
      const next = !this.$store.state.chatSearchOpen
      this.$store.commit('SET_CHAT_SEARCH_OPEN', next)
      if (next && !this.$store.state.workspaceOpen) {
        this.$store.commit('SET_WORKSPACE_OPEN', true)
      }
    },
    openGroupMenu(e) {
      const rect = e.currentTarget.getBoundingClientRect()
      const pos = this.positionMenu(rect, 182, 210)
      this.CLOSE_TELEGRAM_MENUS()
      this.SET_TELEGRAM_GROUP_MENU({ open: true, ...pos, sessionKey: this.currentSessionKey })
    },
    // 气泡级右击：单图/单文件自动带上 clickedImageUrl/clickedFileUrl，多文件让 row 自己处理
    openBubbleReplyMenu(e, msg) {
      const isMultiFile = !!(msg.files && msg.files.length > 1)
      if (isMultiFile) return this.openReplyMenu(e, msg)
      const isImage = msg.kind === 'image' && !!msg.imageUrl
      const hasFile = !isImage && !!msg.fileUrl
      this.openReplyMenu(
        e, msg,
        isImage ? msg.imageUrl : '',
        hasFile ? msg.fileUrl : '',
        hasFile ? (msg.fileName || '文件') : ''
      )
    },
    openReplyMenu(e, msg, clickedImageUrl, clickedFileUrl, clickedFileName) {
      const rect = { left: e.clientX, right: e.clientX, top: e.clientY, bottom: e.clientY }
      const menuH = msg.side === 'right' ? 140 : 100  // 引用回复 + 复制文本
      const pos = this.positionMenu(rect, 150, menuH)
      this.CLOSE_TELEGRAM_MENUS()
      this.SET_TELEGRAM_REPLY_MENU({
        open: true, ...pos,
        name: msg.name || (msg.side === 'right' ? ((this.auth && this.auth.name) || (this.auth && this.auth.phone) || '我') : '未知'),
        text: msg.text || '',
        isMine: msg.side === 'right',
        messageId: msg.id,
        seq: msg.seq || 0,
        conversationId: this.currentConversationId,
        kind: msg.kind || 'text',
        files: msg.files || null,
        imageUrl: msg.imageUrl || '',
        fileUrl: msg.fileUrl || '',
        fileName: msg.fileName || '',
        mimeType: msg.mimeType || '',
        fileSizeStr: msg.fileSizeStr || '',
        imageWidth: msg.imageWidth || 0,
        imageHeight: msg.imageHeight || 0,
        fileSize: msg.fileSize || 0,
        clickedImageUrl: clickedImageUrl || '',
        clickedFileUrl: clickedFileUrl || '',
        clickedFileName: clickedFileName || ''
      })
    },
    openEmojiMenu(e) {
      const ta = this.$refs.textarea
      const rect = e.currentTarget.getBoundingClientRect()
      // 与 .emoji-menu CSS 对应：8 列 × 40 + 内边距 ≈ 340 宽；max-height 288 + 内边距 ≈ 300 高
      const pos = this.positionMenu(rect, 340, 300, true)
      const context = this.composerContext
      const val = this.inputValue
      const selStart = ta ? ta.selectionStart : val.length
      const selEnd = ta ? ta.selectionEnd : val.length
      this.CLOSE_TELEGRAM_MENUS()
      this.SET_TELEGRAM_EMOJI_MENU({ open: true, ...pos, context, selStart, selEnd, val, textareaId: (ta && ta.id) || null })
    },
    onKeyup(e) {
      if (e.key === 'Escape') { this.CLOSE_TELEGRAM_MENUS(); return }
      this.checkMention()
    },
    autoCaptionResize() {
      const ta = this.$refs.captionInput
      if (!ta) return
      ta.style.height = 'auto'
      ta.style.height = Math.min(ta.scrollHeight, 120) + 'px'
    },
    onCaptionKeydown(e) {
      if (e.isComposing || e.keyCode === 229) return
      if (e.key === 'Escape') { this.CLOSE_TELEGRAM_MENUS(); return }
      // @ 列表打开时，上下键切换选中，Enter/Tab 选中当前项
      if (this.telegramMentionMenu && this.telegramMentionMenu.open && this.telegramMentionMenu.members.length) {
        const members = this.telegramMentionMenu.members
        const cur = this.$store.state.mentionActiveIndex
        if (e.key === 'ArrowDown') { e.preventDefault(); this.$store.commit('SET_MENTION_ACTIVE_INDEX', (cur + 1) % members.length); return }
        if (e.key === 'ArrowUp') { e.preventDefault(); this.$store.commit('SET_MENTION_ACTIVE_INDEX', (cur - 1 + members.length) % members.length); return }
        if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); const m = members[cur]; if (m) this.insertMentionByName(m.name); return }
      }
      if (this._matchHotkey(e, this.hotkeySettings.send)) {
        e.preventDefault()
        this.sendAttachment()
      } else if (this._matchHotkey(e, this.hotkeySettings.newline)) {
        e.preventDefault()
        const ta = e.target
        ta.setRangeText('\n', ta.selectionStart, ta.selectionEnd, 'end')
        this.attachCaption = ta.value
        this.$nextTick(() => { ta.style.height = 'auto'; ta.style.height = Math.min(ta.scrollHeight, 120) + 'px'; ta.scrollTop = ta.scrollHeight })
      } else if (e.key === 'Enter') {
        e.preventDefault()
      }
    },
    checkCaptionMention() {
      if (this.profile && this.profile.type !== 'group') return
      const ta = this.$refs.captionInput
      if (!ta) return
      const val = ta.value
      const pos = ta.selectionStart
      const before = val.slice(0, pos)
      const match = before.match(/@([^@\s]*)$/)
      const captionCtx = 'telegram-caption-' + this.state
      if (!match) {
        this.$store.commit('SET_TELEGRAM_MENTION_MENU', { open: false, x: 0, y: 0, context: '', members: [], atStart: 0, query: '', val: '' })
        return
      }
      const query = match[1].toLowerCase()
      const atStart = before.lastIndexOf('@')
      const members = this.groupMembersList.filter(m => !query || m.name.toLowerCase().includes(query))
      if (!members.length) {
        this.$store.commit('SET_TELEGRAM_MENTION_MENU', { open: false, x: 0, y: 0, context: '', members: [], atStart: 0, query: '', val: '', anchorTop: null })
        return
      }
      const rect = ta.getBoundingClientRect()
      const vw = window.innerWidth
      let left = Math.min(rect.left, vw - 268 - 16)
      left = Math.max(16, left)
      // use captionCtx so insertMention writes back to attachCaption
      this.SET_TELEGRAM_MENTION_MENU({ open: true, x: left, y: rect.top - 200, anchorTop: rect.top, context: captionCtx, members, atStart, query, val, textareaId: ta.id || null })
    },
    checkMention() {
      // 同事私聊不需要@功能
      if (this.profile && this.profile.type !== 'group') return
      const ta = this.$refs.textarea
      if (!ta) return
      const val = ta.value
      const pos = ta.selectionStart
      const before = val.slice(0, pos)
      const match = before.match(/@([^@\s]*)$/)
      if (!match) { this.$store.commit('SET_TELEGRAM_MENTION_MENU', { open: false, x: 0, y: 0, context: '', members: [], atStart: 0, query: '', val: '' }); return }
      const query = match[1].toLowerCase()
      const atStart = before.lastIndexOf('@')
      const members = this.groupMembersList.filter(m => !query || m.name.toLowerCase().includes(query))
      if (!members.length) { this.$store.commit('SET_TELEGRAM_MENTION_MENU', { open: false, x: 0, y: 0, context: '', members: [], atStart: 0, query: '', val: '', anchorTop: null }); return }
      const rect = ta.getBoundingClientRect()
      // left 对齐 textarea 左边缘，top 先给预估值，渲染后由 TelegramMenus 用实际高度校正
      const vw = window.innerWidth
      let left = Math.min(rect.left, vw - 268 - 16)
      left = Math.max(16, left)
      const context = this.composerContext
      this.SET_TELEGRAM_MENTION_MENU({ open: true, x: left, y: rect.top - 200, anchorTop: rect.top, context, members, atStart, query, val, textareaId: ta.id || null })
    },
    toggleVoice() {
      if (this.voiceActive) {
        stopSpeechRecognition(); this.voiceActive = false
        if (this.voiceText) this.SET_COMPOSER_INPUT({ context: this.composerContext, value: this._voiceBaseText + this.voiceText })
        this.voiceText = ''; this.voiceError = ''; return
      }
      const reason = getSpeechUnsupportedReason()
      if (reason) { this.voiceActive = true; this.voiceError = reason; return }
      this.voiceActive = true; this.voiceText = ''; this.voiceError = ''; this._voiceBaseText = this.inputValue || ''
      startSpeechRecognition(
        (text) => { this.voiceText = text },
        () => { if (this.voiceText) this.SET_COMPOSER_INPUT({ context: this.composerContext, value: this._voiceBaseText + this.voiceText }); this.voiceActive = false; this.voiceText = ''; this.voiceError = '' },
        (err) => { this.voiceError = err || '语音识别失败'; this.voiceText = '' }
      )
    },
    openSkillPicker(e) {
      this.$store.commit('SET_MODEL_PICKER_OPEN', false)
      this.$store.commit('SET_SKILL_PICKER_OPEN', { open: true, rect: e.currentTarget.getBoundingClientRect() })
    },
    openModelPicker(e) {
      this.$store.commit('SET_SKILL_PICKER_OPEN', false)
      this.$store.commit('SET_MODEL_PICKER_OPEN', { open: true, rect: e.currentTarget.getBoundingClientRect() })
    },
    removeSkill(id) { this.CLEAR_SKILLS(id) },
    _fileToAttachment(file) {
      const mimeType = file.type || ''
      const ext = (file.name || '').split('.').pop().toLowerCase()
      let kind
      if (mimeType.startsWith('image/')) kind = 'image'
      else if (mimeType.startsWith('video/')) kind = 'video'
      else if (mimeType.startsWith('audio/')) kind = 'audio'
      // 注意：模板只识别 'document' 和 'file'，'markdown' 会落到文本气泡分支
      else if (ext === 'md') kind = 'document'
      else if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) kind = 'document'
      else kind = 'file'
      const bytes = file.size
      const fileSizeStr = bytes > 0
        ? (bytes < 1024 * 1024 ? (bytes / 1024).toFixed(1) + ' KB' : (bytes / (1024 * 1024)).toFixed(1) + ' MB')
        : ''
      const localUrl = (kind === 'image' || kind === 'video') ? URL.createObjectURL(file) : ''
      return { file, kind, localUrl, fileName: file.name || '未命名', fileSizeStr, mimeType }
    },
    _openAttachPanel(files) {
      if (!files || !files.length) return
      const currentText = this.inputValue || ''
      if (currentText.trim() && !this.attachCaption) {
        this.attachCaption = currentText
        this.SET_COMPOSER_INPUT({ context: this.composerContext, value: '' })
        this.$nextTick(() => this.autoResize())
      }
      const newItems = Array.from(files).map(f => this._fileToAttachment(f))
      this.pendingAttachments = [...this.pendingAttachments, ...newItems]
      this.$nextTick(() => {
        if (this.$refs.captionInput) {
          this.$refs.captionInput.focus()
          // 程序化赋值 attachCaption 不会触发 @input，需要手动撑高
          this.autoCaptionResize()
        }
      })
    },
    triggerFileInput() {
      const input = this.$refs.fileInput
      if (!input) return
      // reset value BEFORE click so same files can be re-selected
      try { input.value = '' } catch (e) {}
      input.click()
    },
    handleFileSelect(e) {
      // copy FileList to array BEFORE clearing value (clearing may wipe FileList in some browsers)
      const fileArr = e.target.files ? Array.from(e.target.files) : []
      e.target.value = ''
      if (!fileArr.length) return
      this._openAttachPanel(fileArr)
    },
    onDragLeave(e) {
      // only clear when leaving the shell entirely (not entering a child)
      if (!e.currentTarget.contains(e.relatedTarget)) {
        this.isDragOver = false
      }
    },
    handleDrop(e) {
      this.isDragOver = false
      const files = e.dataTransfer && e.dataTransfer.files
      if (files && files.length) this._openAttachPanel(files)
    },
    handlePaste(e) {
      const items = e.clipboardData && e.clipboardData.items
      if (!items) return
      const fileItems = Array.from(items).filter(it => it.kind === 'file')
      if (!fileItems.length) return  // no files, let normal text paste happen
      const files = fileItems.map(it => it.getAsFile()).filter(Boolean)
      if (!files.length) return
      e.preventDefault()  // only prevent when we actually handle files
      this._openAttachPanel(files)
    },
    removeAttachment(idx) {
      const att = this.pendingAttachments[idx]
      if (att && att.localUrl) URL.revokeObjectURL(att.localUrl)
      this.pendingAttachments.splice(idx, 1)
    },
    onVideoThumb(e) { e.target.currentTime = 0.1 },
    fileKindLabel(att) {
      const ext = (att.fileName || '').split('.').pop().toUpperCase()
      return ext || att.kind.toUpperCase() || '文'
    },
    cancelAttachment() {
      if (this.attachCaption.trim()) {
        this.SET_COMPOSER_INPUT({ context: this.composerContext, value: this.attachCaption })
      }
      this.pendingAttachments.forEach(att => { if (att.localUrl) URL.revokeObjectURL(att.localUrl) })
      this.pendingAttachments = []
      this.attachCaption = ''
      this.$nextTick(() => this.autoResize())
    },
    async sendAttachment() {
      if (!this.pendingAttachments.length) return
      // 滚动到底部 + 会话列表到顶
      const key = this._scrollKey()
      if (key) this._scrollAtBottom[key] = true
      this._scrollToBottom()
      this.$nextTick(() => { const el = document.getElementById('sessionList'); if (el) el.scrollTop = 0 })
      const attachments = [...this.pendingAttachments]
      const caption = this.attachCaption
      const now = new Date()
      const timeStr = now.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })
      this.pendingAttachments = []
      this.attachCaption = ''
      this.$nextTick(() => this.autoResize())
      const convId = this.currentConversationId
      // 在 await 前同步捕获 topicId，避免切换会话后被读到新会话的 topic
      const topicId = this.$store.getters.safeCurrentTopicKey || undefined
      const tempId = 'temp-file-' + Date.now()
      const fileReply = this.pendingReply
      const fileReplyToId = fileReply ? fileReply.messageId : undefined
      if (fileReply) this.clearReply()
      if (convId) {
        const first = attachments[0]
        const tempFiles = attachments.map(att => ({
          type: att.kind, url: att.localUrl || '',
          name: att.fileName, size: att.file.size, mimeType: att.mimeType
        }))
        // 收集临时消息的 mentions 用于 @ 高亮
        const pendingMentions = this.$store.state.pendingMentions || {}
        const tempMentions = []
        const pendingNames = Object.keys(pendingMentions).sort((a, b) => b.length - a.length)
        for (const mName of pendingNames) {
          const p = pendingMentions[mName]
          if (!p) continue
          let pos = 0
          while ((pos = caption.indexOf('@' + mName, pos)) !== -1) {
            tempMentions.push({ id: p.id, type: p.type, name: mName })
            pos += mName.length + 1
          }
        }
        // 同时把 caption 里的 @name 替换成 [@#N] 占位符，作为 tempMsg.rawText
        // —— 渲染 mention 高亮依赖 rawText 含 [@#，没填会让首次渲染算出无 mention 的 HTML 进 LRU 缓存，
        //    后续刷新（fetchConversationMessages）按真 id 命中坏缓存导致 @ 样式丢失
        let tempRawText = caption
        let _rcnt = 0
        for (const mName of pendingNames) {
          const p = pendingMentions[mName]
          if (!p) continue
          while (tempRawText.indexOf('@' + mName) !== -1) {
            _rcnt++
            tempRawText = tempRawText.replace('@' + mName, `[@#${_rcnt}]`)
          }
        }
        const tempMsg = {
          id: tempId,
          side: 'right', name: '', text: caption, rawText: tempRawText, time: timeStr,
          mentions: tempMentions,
          avatarText: (this.auth && this.auth.name) ? this.auth.name.slice(0, 1) : 'Me',
          avatarImage: (this.auth && this.auth.avatarUrl) || null,
          avatarColorRgb: (this.auth && this.auth.avatarColorRgb) || '',
          kind: attachments.length > 1 ? 'multi' : first.kind,
          imageUrl: first.kind === 'image' ? first.localUrl : '',
          fileUrl: first.localUrl, fileName: first.fileName,
          fileSize: first.file.size, fileSizeStr: first.fileSizeStr, mimeType: first.mimeType,
          ext: (first.fileName || '').split('.').pop().toLowerCase(),
          files: tempFiles, status: 'sending', progress: 0,
          replyTo: fileReply ? { id: fileReply.messageId, name: fileReply.name || '', text: fileReply.text || '', files: fileReply.files || null } : null
        }
        this.$store.commit('APPEND_CONVERSATION_MESSAGE', { conversationId: convId, message: tempMsg })
      }
      try {
        if (convId) {
          // 真实进度：各文件/分片 onProgress 按字节加权汇总（断网时 onProgress 停 → 进度条暂停；续传恢复 → 继续涨）
          const _sizes = attachments.map(a => (a.file && a.file.size) || 0)
          const _total = _sizes.reduce((a, b) => a + b, 0) || 1
          const _pct = new Array(attachments.length).fill(0)
          const _commitProgress = () => {
            let loaded = 0
            for (let i = 0; i < attachments.length; i++) loaded += (_pct[i] / 100) * _sizes[i]
            this.$store.commit('UPDATE_MESSAGE_STATUS', { conversationId: convId, messageId: tempId, progress: Math.min(99, Math.round(loaded / _total * 100)) })
          }

          const uploaded = await Promise.all(attachments.map(async (att, i) => {
            const onP = (p) => { _pct[i] = p; _commitProgress() }
            const result = att.kind === 'image'
              ? await api.uploadImage(att.file, onP)
              : await api.uploadFile(att.file, onP)
            const url = result.url || result.file_url || result.fileUrl || ''
            const item = { type: att.kind, name: att.fileName, url, size: att.file.size, mimeType: att.mimeType }
            // 图片：读取宽高传给后端
            if (att.kind === 'image' && att.localUrl) {
              try {
                const dim = await _getImageDimensions(att.localUrl)
                if (dim.width) { item.width = dim.width; item.height = dim.height }
              } catch {}
            }
            return item
          }))
          // 全部上传完成
          this.$store.commit('UPDATE_MESSAGE_STATUS', { conversationId: convId, messageId: tempId, progress: 99 })
          // 处理 caption 里的 @name → [@#n] 占位符 + mentions 数组
          const capMentions = []
          let capCounter = 0
          const capPendingMentions = this.$store.state.pendingMentions || {}
          let sendCaption = caption
          const capPendingNames = Object.keys(capPendingMentions).sort((a, b) => b.length - a.length)
          for (const mName of capPendingNames) {
            const pending = capPendingMentions[mName]
            if (!pending) continue
            const search = '@' + mName
            let pos = 0
            while ((pos = sendCaption.indexOf(search, pos)) !== -1) {
              capCounter++
              capMentions.push({ id: pending.id, type: pending.type || 'user' })
              sendCaption = sendCaption.substring(0, pos) + `[@#${capCounter}]` + sendCaption.substring(pos + search.length)
              pos += `[@#${capCounter}]`.length
            }
          }
          const sendResult = await api.sendMediaMessage(convId, {
            messageType: 'file',
            body: { files: uploaded, caption: sendCaption },
            mentions: capMentions.length ? capMentions : undefined,
            topicId,
            replyToId: fileReplyToId
          })
          if (capMentions.length) this.$store.commit('CLEAR_PENDING_MENTIONS')
          // 立即把 temp id 换成 real id，防止多个 temp-file- 互相替换
          const realMsgId = sendResult && (sendResult.id || sendResult.messageId) ? (sendResult.id || sendResult.messageId) : tempId
          if (realMsgId !== tempId) {
            this.$store.commit('UPDATE_MESSAGE_STATUS', { conversationId: convId, messageId: tempId, id: realMsgId })
          }

          // API 完成，预加载 CDN 图片后再替换 URL（避免闪烁）
          const first = uploaded[0]
          const realUrl = first ? first.url : ''
          if (realUrl && first && (first.type === 'image' || (first.mimeType && first.mimeType.startsWith('image/')))) {
            await _preloadImage(realUrl)
          }
          this.$store.commit('UPDATE_MESSAGE_STATUS', {
            conversationId: convId,
            messageId: realMsgId,
            status: 'sent',
            progress: undefined,
            fileUrl: realUrl,
            imageUrl: (first && (first.type === 'image' || (first.mimeType && first.mimeType.startsWith('image/')))) ? realUrl : '',
            files: uploaded.map(u => ({
              type: u.type, url: u.url, name: u.name,
              size: u.size, mimeType: u.mimeType
            }))
          })
        }
      } catch (err) {
        console.warn('[TelegramChat] 文件发送失败:', err.message)
        if (convId) {
          if (this.currentConversationId === convId) {
            this.$store.commit('UPDATE_MESSAGE_STATUS', { conversationId: convId, messageId: tempId, status: 'failed' })
          } else {
            // 已切走原会话，静默清掉 temp，避免切回看到陌生失败提示
            this.$store.commit('REMOVE_CONVERSATION_MESSAGE', { conversationId: convId, messageId: tempId })
          }
        }
      } finally {
        attachments.forEach(att => { if (att.localUrl) URL.revokeObjectURL(att.localUrl) })
      }
    },
    multiFileRows(files) {
      const rows = []
      for (let i = 0; i < files.length; i += 2) {
        rows.push(files.slice(i, i + 2))
      }
      return rows
    },
    formatSize(bytes) {
      if (!bytes || bytes === 0) return ''
      return bytes < 1024 * 1024
        ? (bytes / 1024).toFixed(1) + ' KB'
        : (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },
    fileExtLabel(fileName) {
      const ext = (fileName || '').split('.').pop().toUpperCase()
      return ext || '文'
    },
    isImageFile(f) {
      if (!f) return false
      if (f.type === 'image') return true
      if (f.mimeType && f.mimeType.startsWith('image/')) return true
      const ext = (f.name || '').split('.').pop().toLowerCase()
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)
    },
    openFileItem(f, msg, fi) {
      const ext = (f.name || '').split('.').pop().toLowerCase()
      const kind = f.type || (f.mimeType && f.mimeType.startsWith('image/') ? 'image' : f.mimeType && f.mimeType.startsWith('video/') ? 'video' : 'file')
      // fileIndex：该文件在消息 files[] 里的下标，与 messageId 一起唯一定位一张图（多图消息切换用）
      const fakeMsg = { ...msg, kind, imageUrl: f.url, fileUrl: f.url, fileName: f.name, mimeType: f.mimeType || '', ext, fileIndex: (fi == null ? 0 : fi) }
      this.openInWorkspace(fakeMsg)
    },
    isMultiSelected(id) {
      return this.multiSelectedMessages.some(m => m.id === id)
    },
    toggleMultiSelect(msg) {
      this.$store.commit('TOGGLE_MULTI_SELECT_MSG', { id: msg.id, seq: msg.seq || 0, name: msg.name || '', text: msg.text || '', kind: msg.kind || 'text', fileName: msg.fileName || '', imageUrl: msg.imageUrl || '', fileUrl: msg.fileUrl || '', mimeType: msg.mimeType || '', fileSizeStr: msg.fileSizeStr || '', imageWidth: msg.imageWidth || 0, imageHeight: msg.imageHeight || 0, fileSize: msg.fileSize || 0 })
    },
    exitMultiSelect() {
      this.$store.commit('EXIT_MULTI_SELECT')
    },
    multiForwardEach() {
      if (!this.multiSelectedMessages.length) return
      const msgs = [...this.multiSelectedMessages].sort((a, b) => (a.seq || 0) - (b.seq || 0))
      const text = msgs.map(m => {
        const label = m.kind === 'image' ? '[图片]' : m.kind === 'file' ? '[文件]' : (m.text || '')
        return (m.name || '未知') + '：' + label
      }).join('\n')
      this.$store.commit('SET_FORWARD_MODAL', {
        open: true, senderName: '多条消息', text, kind: 'multi-forward-each',
        imageUrl: '', fileName: '', files: null,
        _messages: msgs.map(m => ({ ...m }))
      })
      this.$store.commit('EXIT_MULTI_SELECT')
    },
    multiForwardMerge() {
      if (!this.multiSelectedMessages.length) return
      const msgs = [...this.multiSelectedMessages].sort((a, b) => (a.seq || 0) - (b.seq || 0))
      const text = msgs.map(m => {
        const label = m.kind === 'image' ? '[图片]' : m.kind === 'file' ? '[文件]' : (m.text || '')
        return (m.name || '未知') + '：' + label
      }).join('\n')
      this.$store.commit('SET_FORWARD_MODAL', {
        open: true, senderName: '合并转发', text, kind: 'multi-forward-merge',
        imageUrl: '', fileName: '', files: null,
        _messages: msgs.map(m => ({ ...m }))
      })
      this.$store.commit('EXIT_MULTI_SELECT')
    },
    openInWorkspace(msg) {
      const ext = msg.ext || (msg.fileName || '').split('.').pop().toLowerCase()
      const previewItem = {
        id: 'chat-file-preview',
        previewMode: 'file',
        title: msg.fileName || '文件预览',
        meta: msg.fileSizeStr || '',
        url: msg.fileUrl || msg.imageUrl || '',
        kind: msg.kind,
        fileName: msg.fileName || '',
        mimeType: msg.mimeType || '',
        fileSizeStr: msg.fileSizeStr || '',
        ext
      }
      this.$store.commit('SET_GROUP_WORKSPACE_PREVIEW', previewItem)
      this.$store.commit('SET_GROUP_WORKSPACE_TAB', 'preview')
      this.$store.commit('SET_WORKSPACE_OPEN', true)
      if (msg.kind === 'image') {
        // 先顶上单图预览（无延迟），再异步装载"该话题图片画廊"用于左右切换
        this.$store.commit('CLEAR_FULLSCREEN_GALLERY')
        this.$store.commit('SET_FULLSCREEN_PREVIEW', previewItem)
        this._loadImageGallery(msg)
      }
    },
    // 把后端 image item 转成预览项（带画廊定位/游标字段）
    _galleryPreviewItem(it) {
      const ext = (it.fileName || '').split('.').pop().toLowerCase()
      return {
        id: 'chat-file-preview',
        previewMode: 'file',
        title: it.fileName || '图片预览',
        meta: '',
        url: it.url,
        kind: 'image',
        fileName: it.fileName || '',
        mimeType: it.mimeType || 'image/*',
        fileSizeStr: '',
        ext,
        // 画廊定位 + 游标
        _messageId: it.messageId,
        _fileIndex: (it.fileIndex == null ? 0 : it.fileIndex),
        _seq: it.seq
      }
    },
    // 装载首窗：以点开那张图为锚点，取该话题图片列表（仅图片参与切换）
    async _loadImageGallery(msg) {
      const convId = this.currentConversationId
      const topicId = this.currentTopicKey || ''
      const messageId = msg.id
      const anchorFileIndex = (msg.fileIndex == null ? 0 : msg.fileIndex)
      // 无法定位（如转发预览的假消息）→ 退化为单图预览，不建画廊
      if (!convId || !topicId || !messageId) return
      try {
        const data = await api.getTopicImages(convId, topicId, { anchorMessageId: messageId, anchorFileIndex, limit: 30 })
        const d = (data && data.items) ? data : (data && data.data) ? data.data : data
        const items = (d && d.items) || []
        if (items.length <= 1) return   // 只有一张图 → 不建画廊（无左右切换）
        // 用户可能已切到别的图/关掉预览：仅当当前仍在预览本锚点图时才落地画廊
        const cur = this.$store.state.fullscreenPreviewItem
        if (!cur || cur.url !== (msg.fileUrl || msg.imageUrl || '')) return
        const loaded = items.map(it => this._galleryPreviewItem(it))
        let anchorOffset = loaded.findIndex(p => p._messageId === messageId && p._fileIndex === anchorFileIndex)
        if (anchorOffset < 0) anchorOffset = 0
        const anchorIndex = (typeof d.anchorIndex === 'number') ? d.anchorIndex : anchorOffset
        this.$store.commit('SET_FULLSCREEN_GALLERY', {
          convId, topicId, loaded,
          baseIndex: anchorIndex - anchorOffset,   // loaded[0] 的绝对下标
          total: d.total || loaded.length,
          cur: anchorIndex,
          hasOlder: !!d.hasOlder,
          hasNewer: !!d.hasNewer,
          loading: false
        })
        // 对准画廊里的锚点图（确保 url/标题与列表一致）
        this.$store.commit('SET_FULLSCREEN_PREVIEW', loaded[anchorOffset])
      } catch (e) {
        // 接口失败 → 退化为单图预览（已有 SET_FULLSCREEN_PREVIEW 兜底），不报错
        console.warn('[TelegramChat] 装载图片画廊失败', e)
      }
    },
    fileIconLabel(msg) {
      const ext = (msg.ext || (msg.fileName || '').split('.').pop()).toLowerCase()
      if (ext === 'pdf') return 'PDF'
      if (ext === 'doc' || ext === 'docx') return 'DOC'
      if (ext === 'xls' || ext === 'xlsx') return 'XLS'
      if (ext === 'ppt' || ext === 'pptx') return 'PPT'
      if (ext === 'zip' || ext === 'rar' || ext === '7z') return 'ZIP'
      if (ext === 'mp3' || (msg.kind === 'audio')) return '♪'
      if (ext === 'mp4' || (msg.kind === 'video')) return '▶'
      if (ext === 'txt') return 'TXT'
      if (ext === 'md') return 'TXT'
      if (ext === 'html' || ext === 'htm') return 'HTML'
      return '文'
    },
    fileIconClass(msg) {
      const ext = (msg.ext || (msg.fileName || '').split('.').pop()).toLowerCase()
      if (ext === 'pdf') return 'tg-icon-pdf'
      if (ext === 'doc' || ext === 'docx') return 'tg-icon-doc'
      if (ext === 'xls' || ext === 'xlsx') return 'tg-icon-xls'
      if (ext === 'ppt' || ext === 'pptx') return 'tg-icon-ppt'
      if (ext === 'zip' || ext === 'rar' || ext === '7z') return 'tg-icon-zip'
      if (ext === 'txt' || ext === 'md') return 'tg-icon-txt'
      if (ext === 'html' || ext === 'htm') return 'tg-icon-html'
      return 'tg-icon-default'
    },
    retryMessage(msg) {
      const convId = this.currentConversationId
      if (!convId || !msg) return
      this.resendMessage({ conversationId: convId, message: msg })
    },
    openImagePreview(url) {
      if (!url) return
      window.open(url, '_blank', 'noopener')
    },
    _scrollKey() {
      const sk = this.$store.state.currentSessionKey || ''
      const tk = this.$store.state.currentTopicKey || ''
      return sk + (tk ? ':' + tk : '')
    },
    _saveScrollPos() {
      const el = this.$refs.body
      if (!el) return
      const key = this._scrollKey()
      if (!key) return
      this._scrollPositions[key] = el.scrollTop
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight
      this._scrollAtBottom[key] = dist <= 80
      this.showScrollBtn = dist > 200
    },
    _restoreScrollPos() {
      const el = this.$refs.body
      if (!el) return
      this.$nextTick(() => { el.scrollTop = el.scrollHeight })
    },
    async jumpToNextMention() {
      const convId = this.currentConversationId
      if (!convId) return
      const ids = this.mentionedMsgIds[convId] || []
      const idx = this.mentionJumpIndex[convId] || 0
      if (idx >= ids.length) return
      const msgId = ids[idx]
      // 更新索引（Vue.set 保持响应式）
      this.$set(this.mentionJumpIndex, convId, idx + 1)
      // 跳转
      const topicId = this.$store.state.currentTopicKey || undefined
      const found = await this.$store.dispatch('jumpToMessage', { conversationId: convId, topicId, messageId: msgId })
      if (!found) return
      this.$nextTick(() => {
        const el = document.getElementById('msg-' + msgId)
        if (!el) return
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.classList.add('highlight-flash')
        setTimeout(() => el.classList.remove('highlight-flash'), 2000)
      })
    },
    async jumpToUnread() {
      const convId = this.currentConversationId
      const msgId = this.currentFirstUnreadId
      if (!convId || !msgId) return
      this.unreadJumpDismissed = true
      const topicId = this.$store.state.currentTopicKey || undefined
      const found = await this.$store.dispatch('jumpToMessage', { conversationId: convId, topicId, messageId: msgId })
      if (!found) return
      this.$nextTick(() => {
        const el = document.getElementById('unread-divider-' + msgId) || document.getElementById('msg-' + msgId)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },
    async showReadReceipt(e, msg) {
      if (!msg.id) return
      try {
        const data = await api.getMessageReaders(msg.id)
        const rect = e.target.getBoundingClientRect()
        const popupH = 320
        const spaceBelow = window.innerHeight - rect.bottom - 16
        const showAbove = spaceBelow < popupH && rect.top > popupH
        const style = showAbove
          ? { bottom: (window.innerHeight - rect.top + 8) + 'px', right: (window.innerWidth - rect.right) + 'px' }
          : { top: (rect.bottom + 8) + 'px', right: (window.innerWidth - rect.right) + 'px' }
        this.readReceiptPopup = {
          style,
          readCount: (data && data.readCount) || 0,
          unreadCount: (data && data.unreadCount) || 0,
          readers: (data && data.readers) || [],
          unreaders: (data && data.unreaders) || []
        }
      } catch (err) {
        console.warn('[TelegramChat] showReadReceipt failed:', err.message)
      }
    },
    formatReadTime(t) {
      if (!t) return ''
      try {
        const d = new Date(t)
        return d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })
      } catch { return '' }
    },
    _closeReadReceipt() {
      this.readReceiptPopup = null
    },
    imgPlaceholderStyle(msg) {
      if (!msg.imageWidth || !msg.imageHeight) return {}
      let w = msg.imageWidth, h = msg.imageHeight
      if (w > 260) { h = h * 260 / w; w = 260 }
      if (h > 300) { w = w * 300 / h; h = 300 }
      return { width: Math.round(w) + 'px', height: Math.round(h) + 'px' }
    },
    onImageLoad() {
      const el = this.$refs.body
      if (!el) return
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight
      if (dist < 300) el.scrollTop = el.scrollHeight
    },
    async scrollToLatest() {
      const convId = this.currentConversationId
      const topicId = this.$store.state.currentTopicKey || undefined
      // 跳转窗口下：直接重拉最新一页覆盖 raw 数组（对齐 Telegram"切换 view"行为）
      // fetchConversationMessages 内部会清掉 conversationHasMoreAfter / gapAfterId / latestSegmentIds
      if (convId && this.$store.state.conversationHasMoreAfter[convId]) {
        await this.$store.dispatch('fetchConversationMessages', { conversationId: convId, topicId })
      }
      const key = this._scrollKey()
      if (key) this._scrollAtBottom[key] = true
      this.showScrollBtn = false
      this._scrollToBottom()
    },
    _scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.body) this.$refs.body.scrollTop = this.$refs.body.scrollHeight
      })
    },
    // 切会话/切话题/组件重建时强制把流式显示长度对齐到当前 streaming content 真实长度
    // 修复：群聊里 agent 流式回复时切走再切回，看起来从头打字
    _syncStreamRendered() {
      if (this._typewriterTimer) {
        clearInterval(this._typewriterTimer)
        this._typewriterTimer = null
      }
      const sm = this.currentStreamingMessage
      this.streamRenderedLen = (sm && sm.content) ? sm.content.length : 0
      this._streamSwitchedAway = false
    },
    _onBodyScroll() {
      if (this.readReceiptPopup) this.readReceiptPopup = null
      this._saveScrollPos()
      // 滚到分隔线时隐藏未读跳转按钮
      if (this.unreadJumpVisible && this.currentFirstUnreadId) {
        const divider = document.getElementById('unread-divider-' + this.currentFirstUnreadId)
        if (divider) {
          const rect = divider.getBoundingClientRect()
          const bodyRect = this.$refs.body.getBoundingClientRect()
          if (rect.top >= bodyRect.top && rect.bottom <= bodyRect.bottom) {
            this.unreadJumpDismissed = true
          }
        }
      }
      const el = this.$refs.body
      if (!el) return
      const convId = this.currentConversationId
      // 跳转窗口下：滚到底部一屏内 → 触发 after 拉中间
      // 用 conversationLoadingAfter 锁防并发，hasMoreAfter=false 后停
      if (convId &&
          this.$store.state.conversationHasMoreAfter[convId] &&
          !this.$store.state.conversationLoadingAfter[convId]) {
        const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
        if (distFromBottom < 800) {
          const topicId = this.$store.getters.safeCurrentTopicKey || undefined
          this.$store.dispatch('loadMessagesAfter', { conversationId: convId, topicId })
        }
      }
      // 滚到顶部加载更多历史消息
      if (el.scrollTop > 50) return
      if (!convId) return
      if (this.$store.state.conversationMessagesLoading[convId]) return
      if (!this.$store.state.conversationMessagesHasMore[convId]) return
      // 跳转窗口未填满时不触发 loadMore（scrollIntoView 后 scrollTop≈0 会误触发）
      if (this.$store.state.conversationHasMoreAfter[convId]) return
      if (this.$store.state.conversationLoadingAfter[convId]) return
      const oldHeight = el.scrollHeight
      this.$store.dispatch('fetchConversationMessages', {
        conversationId: convId,
        topicId: this.$store.getters.safeCurrentTopicKey || undefined,
        loadMore: true
      }).then(() => {
        this.$nextTick(() => {
          el.scrollTop = el.scrollHeight - oldHeight
        })
      })
    }
  },
  watch: {
    composerContext() {
      this.$nextTick(() => this.autoResize())
    },
    // 消息加载完后 firstUnreadMessageId 才有值，此时快照未读数
    currentFirstUnreadId(val) {
      if (val && this.unreadJumpSnapshot === 0 && !this.unreadJumpDismissed) {
        const convId = this.currentConversationId
        // 有话题时优先用话题级未读，否则用会话级未读
        const si = convId ? this.$store.state.sessionItems.find(s => s.conversationId === convId) : null
        let fromSession = 0
        const topicKey = this.currentTopicKey
        if (topicKey && si && si.topicKey) {
          const page = this.sessionTopicPages[si.topicKey]
          const topic = page && page.topics.find(t => t.key === topicKey)
          fromSession = (topic && topic.unread) || 0
        }
        if (!fromSession) {
          fromSession = (si && si.unread) || (convId ? (this.unreadByConversation[convId] || 0) : 0)
        }
        if (fromSession > 0) {
          this.unreadJumpSnapshot = fromSession
        } else {
          const msgs = this.messages || []
          const idx = msgs.findIndex(m => m.id === val)
          if (idx >= 0) this.unreadJumpSnapshot = msgs.length - idx
        }
      }
    },
    currentStreamingMessage(val) {
      if (!val) {
        if (this._typewriterTimer) { clearInterval(this._typewriterTimer); this._typewriterTimer = null }
        this._streamSwitchedAway = true
        return
      }
      if (!val.content) return
      var contentLen = val.content.length
      if (this.streamRenderedLen > contentLen) {
        this.streamRenderedLen = 0
      }
      if (this._streamSwitchedAway) {
        this._streamSwitchedAway = false
        this.streamRenderedLen = contentLen
        return
      }
      if (this.streamRenderedLen >= contentLen) {
        this.streamRenderedLen = contentLen
        return
      }
      if (!this._typewriterTimer) {
        this._typewriterTimer = setInterval(() => {
          var target = (this.currentStreamingMessage && this.currentStreamingMessage.content) || ''
          if (this.streamRenderedLen < target.length) {
            this.streamRenderedLen += 1
            var el = this.$refs.body
            if (el) {
              var dist = el.scrollHeight - el.scrollTop - el.clientHeight
              if (dist < 100) el.scrollTop = el.scrollHeight
            }
          } else {
            clearInterval(this._typewriterTimer)
            this._typewriterTimer = null
          }
        }, 20)
      }
    },
    onlineMembersRefreshAt(val) {
      if (val && this.currentConversationId) {
        this._fetchOnlineCount(this.currentConversationId)
      }
    },
    onlineCountVersion(val) {
      if (val && this.currentConversationId) {
        this._fetchOnlineCount(this.currentConversationId)
      }
    },
    currentConversationId: {
      immediate: true,
      handler(newId, oldId) {
        // 切换会话时关闭聊天搜索面板
        if (oldId && newId !== oldId && this.$store.state.chatSearchOpen) {
          this.$store.commit('SET_CHAT_SEARCH_OPEN', false)
        }
        // 保存旧会话滚动位置
        if (oldId) this._saveScrollPos()
        this._stopOnlineCountTimer()
        if (newId && this.profile && (this.profile.type === 'group' || this.profile.type === 'coworker')) {
          this._fetchOnlineCount(newId)
          this.onlineCountTimer = setInterval(() => this._fetchOnlineCount(newId), 30000)
        } else {
          this.onlineCount = null
        }
        // 恢复新会话滚动位置
        if (newId) this._restoreScrollPos()
        // 重置未读跳转状态，快照当前未读数（话题级优先）
        this.unreadJumpDismissed = false
        this.unreadJumpSnapshot = this._getTopicAwareUnread(newId)
        this.readReceiptPopup = null
        // 切会话时强制把流式显示长度对齐到当前 streaming content 长度，避免切回时从头打字重放
        this._syncStreamRendered()
      }
    },
    currentTopicKey(newKey, oldKey) {
      if (newKey !== oldKey) {
        this.unreadJumpDismissed = false
        this.unreadJumpSnapshot = this._getTopicAwareUnread(this.currentConversationId)
        this._syncStreamRendered()
      }
    },
    messages(newMsgs, oldMsgs) {
      const newLen = newMsgs ? newMsgs.length : 0
      const oldLen = oldMsgs ? oldMsgs.length : 0
      const newLastId = newMsgs && newMsgs[newLen - 1] ? newMsgs[newLen - 1].id : null
      const oldLastId = oldMsgs && oldMsgs[oldLen - 1] ? oldMsgs[oldLen - 1].id : null
      if (newLen !== oldLen || newLastId !== oldLastId) {
        // 在底部附近才自动滚动，否则保持位置
        const key = this._scrollKey()
        if (!this._scrollAtBottom || this._scrollAtBottom[key] !== false) {
          this._scrollToBottom()
        }
      }
    },
    // sync mention selection back to attachCaption when attachment panel is open
    // 用 captionContextValue computed 代替 deep watch，只在特定 key 变化时触发
    captionContextValue(newVal) {
      if (newVal && this.pendingAttachments.length) {
        this.attachCaption = newVal
        const captionCtx = 'telegram-caption-' + this.state
        this.$store.commit('SET_COMPOSER_INPUT', { context: captionCtx, value: '' })
        this.$nextTick(() => {
          const ta = this.$refs.captionInput
          if (ta) {
            ta.focus()
            ta.selectionStart = ta.selectionEnd = newVal.length
          }
        })
      }
    }
  },
  mounted() {
    this._scrollPositions = {}
    this._scrollAtBottom = {}
    this._lastUserGesture = 0
    this._markUserGesture = () => { this._lastUserGesture = Date.now() }
    this.autoResize()
    if (this.$refs.body) {
      this.$refs.body.scrollTop = this.$refs.body.scrollHeight
      this.$refs.body.addEventListener('click', this.handleBodyClick)
      this.$refs.body.addEventListener('scroll', this._onBodyScroll, { passive: true })
      this.$refs.body.addEventListener('wheel', this._markUserGesture, { passive: true })
      this.$refs.body.addEventListener('touchmove', this._markUserGesture, { passive: true })
      this.$refs.body.addEventListener('keydown', this._markUserGesture)
    }
    // paste listener on the whole shell
    if (this.$el) this.$el.addEventListener('paste', this.handlePaste)
    if (this.$el) this.$el.addEventListener('click', this._onQuickTask)
    document.addEventListener('click', this._closeReadReceipt)
    // 组件 mount 时立即对齐流式显示长度。MainPanel 用 v-if 切换不同 state 段，
    // 跨会话切换会销毁/重建组件，重建后 streamRenderedLen 是 0，而后台 streaming
    // content 已累积 → 否则会从头打字重放
    this._syncStreamRendered()
  },
  beforeDestroy() {
    this._stopOnlineCountTimer()
    if (this.$refs.body) {
      this.$refs.body.removeEventListener('click', this.handleBodyClick)
      this.$refs.body.removeEventListener('scroll', this._onBodyScroll)
      if (this._markUserGesture) {
        this.$refs.body.removeEventListener('wheel', this._markUserGesture)
        this.$refs.body.removeEventListener('touchmove', this._markUserGesture)
        this.$refs.body.removeEventListener('keydown', this._markUserGesture)
      }
    }
    if (this.$el) this.$el.removeEventListener('paste', this.handlePaste)
    if (this.$el) this.$el.removeEventListener('click', this._onQuickTask)
    document.removeEventListener('click', this._closeReadReceipt)
  }
  // NOTE: removed `updated()` hook — scroll is now driven by the `messages` watcher only
}
</script>